import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Badge,
  Form,
  Modal,
  ProgressBar,
  Dropdown,
  ButtonGroup,
  Row,
  Col,
  Spinner,
  Nav,
  Tab,
} from 'react-bootstrap';
import {
  FiArrowLeft,
  FiCheck,
  FiEdit3,
  FiRefreshCw,
  FiAlertCircle,
  FiActivity,
  FiClipboard,
  FiTrendingUp,
  FiTrendingDown,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiChevronUp,
  FiCheckCircle,
  FiSearch,
  FiX,
  FiEye,
  FiCode,
  FiInfo,
  FiBold,
  FiItalic,
  FiList,
  FiMinus,
  FiVolume2,
  FiVolumeX,
  FiMail,
  FiDownload,
  FiXCircle,
  FiImage,
  FiSave,
  FiAlertTriangle,
} from 'react-icons/fi';
import { LuHeading1, LuHeading2, LuHeading3, LuListOrdered } from 'react-icons/lu';
import { analyteStatus, rangePercentage } from '../utils/analyteStatus';
import { esperarRegeneracion } from '../utils/esperarRegeneracion';
import ReactMarkdown from 'react-markdown';
import { marked } from 'marked';
import { interpretationsApi, ttsApi } from '../services/api';
import { DeliveryPolicy, Interpretation, PatientInfo, TTSSection, RejectionReason, OrderType, GabStudiesData } from '../types';
import { LoadingSpinner, StatusBadge, WysiwygEditor, AuditorSignature, AuditTimeline, AuditTimelineButton, GabStudiesPanel } from '../components/common';
import ReopenModal from '../components/interpretation/ReopenModal';
import { useToast } from '../contexts/ToastContext';
import { useAuditLogger } from '../hooks';
import { useAuth } from '../auth/AuthContext';
import { format, parseISO, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { isReviewWindowExpired } from '../utils/reviewWindow';
import { campaignLine } from '../utils/origin';
import { htmlToMarkdown } from '../utils/markdown';

// Safe date formatter that handles invalid dates
const safeFormatDate = (dateStr: string | undefined | null, formatStr: string): string => {
  if (!dateStr) return '-';
  try {
    const date = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
    if (!isValid(date)) return '-';
    return format(date, formatStr, { locale: es });
  } catch {
    return '-';
  }
};

// ----------------------------------------------------------------------------
// Header demographics (E99-F13)
//
// `sex` reaches this screen as `'male'`, `'female'`, `'requires_verification'`,
// `null`, or simply absent -- the last three are records ingested without
// demographics (the `patient_id = "0"` sentinel closed in E99-F04). The header
// used to render `sex === 'male' ? 'Masculino' : 'Femenino'`, so all three of
// those painted **"Femenino"**, and `{age} años` with a null age painted
// " años". A real 40-year-old man was shown to the auditor as a woman with no
// age. So only an explicit `'male'` / `'female'` may yield a sex: this is a
// whitelist, and everything outside it says out loud that there is no datum.
//
// NOTE, because this file holds the OPPOSITE pattern in the same header block
// (`detail-campaign-line`, right under the meta line these two feed): the
// campaign line is not rendered AT ALL when it is missing (R9 of E02-F03).
// That is right for the campaign -- an absent origin is noise, and it is the
// majority case. It would be wrong here. A missing age and sex is not a blank
// to tidy away: it is the fact that the interpretation was produced BLIND, and
// 7 of the 13 affected records carry a flag from the AI itself saying it had
// neither. The auditor has to see that before approving, so the gap is
// PAINTED, never hidden.
// ----------------------------------------------------------------------------
const NO_AGE = 'Edad sin especificar';
const NO_SEX = 'Sexo sin especificar';

const ageLabel = (age: PatientInfo['age']): string =>
  typeof age === 'number' && Number.isFinite(age) && age >= 0 ? `${age} años` : NO_AGE;

const sexLabel = (sex: PatientInfo['sex']): string => {
  if (sex === 'male') return 'Masculino';
  if (sex === 'female') return 'Femenino';
  return NO_SEX;
};

// ----------------------------------------------------------------------------
// Which demographics the AI did NOT have when it read this case (E99-F14).
//
// Thirteen production interpretations were generated without age and sex, and all
// thirteen were approved -- eight of them carrying a flag from the AI itself saying
// it was reading blind. The defect is not the missing datum, it is that a case like
// that can be SIGNED with no friction. Age and sex move the clinical reference
// ranges, so an interpretation produced without them is not the same reading with a
// tidier header: it is a reading made without two variables that change the result.
//
// The criterion is NOT written again here: it is READ OFF the labels the header
// already paints. `ageLabel`/`sexLabel` (E99-F13) are the canonical definition of
// what counts as known -- a finite non-negative number, and `'male'` or `'female'`
// only, which is why `'requires_verification'` counts as ABSENT. A second predicate
// would be a latent defect: the day one of the two moved, the screen and this gate
// would disagree about the same record.
//
// Deliberate, per E99-F14: this reads the demographics as they are TODAY, not as
// they were at generation time. The thirteen affected records were filled in by hand
// (`backfill-demograficos.py`) and now carry age and sex, so they will NOT trigger
// the confirmation. That is accepted -- those thirteen are already approved, and a
// record repaired before the auditor signs it is one where the header the auditor
// reads is true. The gate is for the next blind case, not for the archive. Nothing
// in the payload distinguishes "was born with demographics" from "was backfilled",
// so making it warn anyway would need a new backend field, which is out of scope.
const missingDemographics = (patient?: PatientInfo | null): string[] => {
  const missing: string[] = [];
  if (ageLabel(patient?.age) === NO_AGE) missing.push('la edad');
  if (sexLabel(patient?.sex) === NO_SEX) missing.push('el sexo');
  return missing;
};

// ============================================================================
// DEMO MODE - Set to true to see mock data with multiple studies
// ============================================================================
const USE_DEMO_DATA = false;

const DEMO_INTERPRETATION: Interpretation = {
  interpretation_id: 'INT-DEMO-001',
  patient_id: 'PAT-DEMO-001',
  status: 'pending_review',
  created_at: new Date().toISOString(),
  source_system: 'Sistema Demo',
  test_types: ['LAB_RESULTS', 'HRA_CARDIOVASCULAR', 'PHQ9', 'HRA_DIABETES'],
  ai_confidence: 0.87,
  ai_flags: [
    'Colesterol LDL elevado - riesgo cardiovascular',
    'Glucosa en ayunas elevada - prediabetes',
    'Presión arterial elevada',
    'Síntomas depresivos moderados detectados',
  ],
  ai_interpretation: `## Resumen Clínico

El paciente presenta un **perfil de riesgo cardiometabólico moderado-alto** que requiere intervención.

### Hallazgos Principales

#### Perfil Lipídico
- **Colesterol Total elevado** (245 mg/dL): Supera el límite deseable de 200 mg/dL
- **LDL elevado** (165 mg/dL): Significativamente por encima del objetivo (<100 mg/dL para alto riesgo)
- **HDL bajo** (38 mg/dL): Factor de riesgo independiente (ideal >40 mg/dL en hombres)
- **Triglicéridos elevados** (210 mg/dL): Asociado a síndrome metabólico

#### Panel Metabólico
- **Glucosa en ayunas** (118 mg/dL): Rango de prediabetes (100-125 mg/dL)
- **HbA1c** (6.2%): Confirma estado prediabético

#### Función Renal
- Creatinina y BUN dentro de límites normales
- TFG estimada adecuada para la edad

#### Función Hepática
- Enzimas hepáticas normales
- Sin evidencia de esteatosis

### Evaluación de Riesgo Cardiovascular
El score de riesgo cardiovascular a 10 años es **MODERADO-ALTO** basado en:
- Dislipidemia mixta
- Estado prediabético
- Hipertensión sistólica

### Evaluación de Salud Mental
El cuestionario PHQ-9 indica **síntomas depresivos moderados** (puntuación 14/27), lo cual puede afectar la adherencia al tratamiento y requiere seguimiento.

---

> **Nota importante**: Se recomienda evaluación presencial para confirmar hallazgos y establecer plan terapéutico individualizado.
`,
  raw_data: {
    patient_info: {
      name: 'Roberto García Mendoza',
      age: 52,
      sex: 'male',
    },
    tests: [
      // Estudio 1: Perfil Lipídico
      {
        type: 'LAB_RESULTS',
        study_name: 'Perfil Lipídico',
        analytes: [
          { name: 'Colesterol Total', value: 245, unit: 'mg/dL', reference_min: 0, reference_max: 200 },
          { name: 'Colesterol LDL', value: 165, unit: 'mg/dL', reference_min: 0, reference_max: 100 },
          { name: 'Colesterol HDL', value: 38, unit: 'mg/dL', reference_min: 40, reference_max: 100 },
          { name: 'Triglicéridos', value: 210, unit: 'mg/dL', reference_min: 0, reference_max: 150 },
          { name: 'VLDL', value: 42, unit: 'mg/dL', reference_min: 0, reference_max: 30 },
          { name: 'Índice Aterogénico', value: 4.3, unit: '', reference_min: 0, reference_max: 3.5 },
        ],
      },
      // Estudio 2: Panel Metabólico - With clinical classification data
      {
        type: 'LAB_RESULTS',
        study_name: 'Panel Metabólico',
        analytes: [
          {
            name: 'Glucosa Ayunas',
            value: 118,
            unit: 'mg/dL',
            reference_min: 70,
            reference_max: 126,  // Lab range is wider
            // Clinical classification shows prediabetes
            has_clinical_rules: true,
            clinical_status: 'prediabetes',
            severity: 'warning',
            clinical_ranges: {
              'hipoglucemia': { display: '< 70', severity: 'critical', is_current: false },
              'normal': { display: '70 - 99', severity: 'normal', is_current: false },
              'prediabetes': { display: '100 - 125', severity: 'warning', is_current: true },
              'diabetes': { display: '> 126', severity: 'critical', is_current: false },
            },
            guide: 'ADA 2024',
            interpretation_hint: 'Valor en rango de prediabetes. Requiere intervención de estilo de vida.',
          },
          {
            name: 'HbA1c',
            value: 6.2,
            unit: '%',
            reference_min: 4.0,
            reference_max: 5.6,
            has_clinical_rules: true,
            clinical_status: 'prediabetes',
            severity: 'warning',
            clinical_ranges: {
              'normal': { display: '< 5.7', severity: 'normal', is_current: false },
              'prediabetes': { display: '5.7 - 6.4', severity: 'warning', is_current: true },
              'diabetes': { display: '> 6.5', severity: 'critical', is_current: false },
            },
            guide: 'ADA 2024',
            interpretation_hint: 'Confirma estado prediabético. Refleja control glucémico de 2-3 meses.',
          },
          { name: 'Insulina', value: 18, unit: 'µU/mL', reference_min: 2.6, reference_max: 24.9 },
          { name: 'HOMA-IR', value: 5.2, unit: '', reference_min: 0, reference_max: 2.5 },
          {
            name: 'Ácido Úrico',
            value: 7.1,
            unit: 'mg/dL',
            reference_min: 3.5,
            reference_max: 7.2,
            has_clinical_rules: true,
            clinical_status: 'elevado',
            severity: 'warning',
            clinical_ranges: {
              'bajo': { display: '< 3.0', severity: 'warning', is_current: false },
              'normal': { display: '3.0 - 7.0', severity: 'normal', is_current: false },
              'elevado': { display: '7.0 - 9.0', severity: 'warning', is_current: true },
              'muy elevado (riesgo gota)': { display: '> 9.0', severity: 'critical', is_current: false },
            },
            guide: 'ACR 2020',
            interpretation_hint: 'Valor ligeramente elevado. Evaluar función renal y riesgo de gota.',
          },
        ],
      },
      // Estudio 3: Función Renal
      {
        type: 'LAB_RESULTS',
        study_name: 'Función Renal',
        analytes: [
          { name: 'Creatinina', value: 1.0, unit: 'mg/dL', reference_min: 0.7, reference_max: 1.3 },
          { name: 'BUN', value: 18, unit: 'mg/dL', reference_min: 7, reference_max: 20 },
          { name: 'TFG Estimada', value: 82, unit: 'mL/min', reference_min: 90, reference_max: 120 },
          { name: 'Relación BUN/Cr', value: 18, unit: '', reference_min: 10, reference_max: 20 },
        ],
      },
      // Estudio 4: Función Hepática
      {
        type: 'LAB_RESULTS',
        study_name: 'Función Hepática',
        analytes: [
          { name: 'AST (TGO)', value: 28, unit: 'U/L', reference_min: 0, reference_max: 40 },
          { name: 'ALT (TGP)', value: 32, unit: 'U/L', reference_min: 0, reference_max: 41 },
          { name: 'GGT', value: 45, unit: 'U/L', reference_min: 0, reference_max: 60 },
          { name: 'Fosfatasa Alcalina', value: 78, unit: 'U/L', reference_min: 40, reference_max: 129 },
          { name: 'Bilirrubina Total', value: 0.8, unit: 'mg/dL', reference_min: 0.1, reference_max: 1.2 },
          { name: 'Proteínas Totales', value: 7.2, unit: 'g/dL', reference_min: 6.4, reference_max: 8.3 },
          { name: 'Albúmina', value: 4.5, unit: 'g/dL', reference_min: 3.5, reference_max: 5.0 },
        ],
      },
      // Cuestionario 1: Riesgo Cardiovascular
      {
        type: 'HRA_CARDIOVASCULAR',
        test_type: 'HRA_CARDIOVASCULAR',
        score: 68,
        max_score: 100,
        risk_level: 'high',
        risk_factors: ['hipertension', 'dislipidemia', 'sedentarismo', 'antecedentes_familiares'],
      },
      // Cuestionario 2: PHQ-9 (Depresión)
      {
        type: 'PHQ9',
        test_type: 'PHQ9',
        score: 14,
        max_score: 27,
        risk_level: 'moderate',
        severity: 'moderate',
        risk_factors: ['fatiga', 'alteracion_sueno', 'anhedonia'],
      },
      // Cuestionario 3: Riesgo Diabetes
      {
        type: 'HRA_DIABETES',
        test_type: 'HRA_DIABETES',
        score: 72,
        max_score: 100,
        risk_level: 'high',
        risk_factors: ['prediabetes', 'obesidad_abdominal', 'sedentarismo', 'antecedentes_familiares'],
      },
      // Cuestionario 4: NOM-035
      {
        type: 'NOM035',
        test_type: 'NOM035',
        score: 45,
        max_score: 100,
        risk_level: 'medium',
        risk_factors: ['carga_trabajo', 'jornadas_extendidas'],
      },
    ],
  },
  action_plan: {
    priority: 'high',
    summary: 'Plan integral para reducción de riesgo cardiometabólico con enfoque en modificación de estilo de vida y evaluación de necesidad de tratamiento farmacológico.',
    recommendations: [
      {
        category: 'consulta',
        title: 'Evaluación Cardiológica',
        description: 'Consulta con cardiólogo para evaluación de riesgo cardiovascular detallada, considerar prueba de esfuerzo y ecocardiograma.',
        timeframe: '2 semanas',
      },
      {
        category: 'estilo_vida',
        title: 'Modificación Dietética',
        description: 'Dieta mediterránea baja en grasas saturadas. Reducir carbohidratos refinados. Aumentar consumo de fibra, omega-3 y vegetales.',
        timeframe: 'Inmediato',
      },
      {
        category: 'estilo_vida',
        title: 'Programa de Ejercicio',
        description: 'Iniciar actividad física aeróbica moderada 150 min/semana. Caminata rápida o natación recomendadas.',
        timeframe: '1 semana',
      },
      {
        category: 'consulta',
        title: 'Evaluación Psicológica',
        description: 'Referencia a psicología clínica para evaluación de síntomas depresivos y manejo integral.',
        timeframe: '2 semanas',
      },
      {
        category: 'seguimiento',
        title: 'Control de Presión Arterial',
        description: 'Monitoreo domiciliario de presión arterial. Registro diario durante 2 semanas.',
        timeframe: 'Inmediato',
      },
    ],
    follow_up: {
      suggested_tests: [
        'Perfil lipídico control',
        'Glucosa ayunas',
        'HbA1c',
        'Microalbuminuria',
        'Electrocardiograma',
      ],
      suggested_date: '6 semanas',
    },
  },
};

// Build tests from group_interpretations for hierarchical interpretations
// This handles older interpretations that don't have raw_data.tests but have group_interpretations
const buildTestsFromGroups = (groupInterpretations: any[]): any[] => {
  if (!groupInterpretations || !Array.isArray(groupInterpretations)) {
    return [];
  }

  const tests: any[] = [];

  for (const group of groupInterpretations) {
    // Extract tests from group's input data
    const groupTests = group.tests || group.input_data?.tests || [];

    for (const test of groupTests) {
      // Avoid duplicates by checking if we already have this test
      const isDuplicate = tests.some(
        (t) =>
          t.study_name === test.study_name ||
          (t.analytes &&
            test.analytes &&
            JSON.stringify(t.analytes) === JSON.stringify(test.analytes))
      );

      if (!isDuplicate) {
        tests.push({
          ...test,
          type: test.type || test.test_type || 'LAB_RESULTS',
        });
      }
    }
  }

  return tests;
};

// Get study name from test data
const getStudyName = (test: any): string => {
  // Try to get study name from various fields
  if (test.study_name) return test.study_name;
  if (test.panel_name) return test.panel_name;
  if (test.name) return test.name;

  // Infer from analytes if possible
  const analytes = test.analytes || [];
  if (analytes.length > 0) {
    // Check for common panel patterns
    const names = analytes.map((a: any) => a.name?.toLowerCase() || '');
    if (names.some((n: string) => n.includes('colesterol') || n.includes('hdl') || n.includes('ldl'))) {
      return 'Perfil Lipídico';
    }
    if (names.some((n: string) => n.includes('glucosa') || n.includes('hba1c'))) {
      return 'Panel Metabólico';
    }
    if (names.some((n: string) => n.includes('hemoglobina') || n.includes('hematocrito'))) {
      return 'Biometría Hemática';
    }
    if (names.some((n: string) => n.includes('creatinina') || n.includes('urea'))) {
      return 'Función Renal';
    }
    if (names.some((n: string) => n.includes('ast') || n.includes('alt') || n.includes('bilirrubina'))) {
      return 'Función Hepática';
    }
  }

  return 'Estudio de Laboratorio';
};

// Format large numbers for display (e.g., 250000 → 250K)
const formatNumber = (value: number | string): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);

  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 10000) {
    return (num / 1000).toFixed(0) + 'K';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  // For decimals, limit to 2 decimal places
  if (num % 1 !== 0) {
    return num.toFixed(2).replace(/\.?0+$/, '');
  }
  return String(num);
};

// Format reference range values
const formatRangeValue = (value: number | string | undefined): string => {
  if (value === undefined || value === null) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);

  if (num >= 10000) {
    return (num / 1000).toFixed(0) + 'K';
  }
  return String(num);
};

// Lab Study Carousel - Paginated by STUDY (not by analytes)
const LabStudyCarousel: React.FC<{ labTests: any[] }> = ({ labTests }) => {
  const [currentStudy, setCurrentStudy] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  const totalStudies = labTests.length;

  const toggleFlip = (cardKey: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFlippedCards((prev) => {
      const next = new Set(prev);
      if (next.has(cardKey)) {
        next.delete(cardKey);
      } else {
        next.add(cardKey);
      }
      return next;
    });
  };

  // Collect all analytes from all studies for search
  const allAnalytes = labTests.flatMap((test, studyIdx) =>
    (test.analytes || []).map((analyte: any) => ({
      ...analyte,
      studyIdx,
      studyName: getStudyName(test),
    }))
  );

  const goToStudy = (index: number) => {
    if (index >= 0 && index < totalStudies) {
      setCurrentStudy(index);
      setSearchQuery('');
      setIsSearching(false);
    }
  };

  // Filter analytes based on search
  const filteredAnalytes = searchQuery.trim()
    ? allAnalytes.filter((analyte) =>
        analyte.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : [];

  const currentTest = labTests[currentStudy];
  const analytes = currentTest?.analytes || [];
  const studyName = getStudyName(currentTest);

  // Determine which analytes to display
  const displayAnalytes = isSearching && searchQuery.trim()
    ? filteredAnalytes
    : analytes;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setIsSearching(value.trim().length > 0);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
  };

  return (
    <div className="lab-carousel">
      {/* Study Header with Dropdown Navigation */}
      {!isSearching && (
        <div className="study-header">
          {totalStudies > 1 ? (
            <Dropdown className="study-dropdown">
              <Dropdown.Toggle variant="link" className="study-dropdown-toggle">
                <span className="study-name">{studyName}</span>
                <FiChevronDown className="dropdown-icon" />
              </Dropdown.Toggle>
              <Dropdown.Menu className="study-dropdown-menu">
                {labTests.map((test, idx) => (
                  <Dropdown.Item
                    key={idx}
                    active={currentStudy === idx}
                    onClick={() => goToStudy(idx)}
                    className="study-dropdown-item"
                  >
                    <span className="study-item-name">{getStudyName(test)}</span>
                    <Badge bg="light" text="muted" className="study-item-count">
                      {(test.analytes || []).length}
                    </Badge>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <h6 className="study-name">{studyName}</h6>
          )}
          <Badge bg="light" text="dark" className="analyte-count">
            {analytes.length} biomarcadores
          </Badge>
        </div>
      )}

      {/* Search Results Header */}
      {isSearching && (
        <div className="search-results-header">
          <span className="search-label">Resultados de búsqueda</span>
          <button className="back-to-studies" onClick={clearSearch}>
            <FiX /> Volver a estudios
          </button>
        </div>
      )}

      {/* Search Bar - Below category dropdown */}
      <div className="analyte-search">
        <div className="search-input-wrapper">
          <FiSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Buscar analito..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          {searchQuery && (
            <button className="search-clear" onClick={clearSearch}>
              <FiX />
            </button>
          )}
        </div>
        {isSearching && (
          <div className="search-results-count">
            {filteredAnalytes.length} resultado{filteredAnalytes.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Analytes Grid */}
      <div className="lab-results-grid">
        {displayAnalytes.length === 0 ? (
          <div className="no-results">
            <FiSearch />
            <span>No se encontraron analitos con "{searchQuery}"</span>
          </div>
        ) : (
          displayAnalytes.map((analyte: any, idx: number) => {
            // Un rango `0 - 0` es el centinela de «sin rango publicado», no un intervalo:
            // leerlo literalmente pintaba de rojo 4 246 analitos que el laboratorio marcó
            // normales. La regla vive en analyteStatus, junto a su medición.
            const status = analyteStatus(analyte);
            const isLow = status === 'low';
            const isHigh = status === 'high';
            const isNormal = status === 'normal';
            const isUnknown = status === 'unknown';
            const percentage = rangePercentage(analyte);

            // Clinical classification data
            const hasClinicalRules = analyte.has_clinical_rules === true;
            const cardKey = `${currentStudy}-${idx}`;
            const isFlipped = flippedCards.has(cardKey);

            // Show clinical back view
            if (hasClinicalRules && isFlipped) {
              return (
                <div
                  key={idx}
                  className={`lab-result-item lab-result-clinical ${isLow ? 'status-low' : isHigh ? 'status-high' : 'status-normal'}`}
                  onClick={(e) => toggleFlip(cardKey, e)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="lab-result-status">
                    <FiInfo />
                  </span>
                  <div className="lab-result-header">
                    <span className="lab-result-name">{analyte.clinical_status}</span>
                    <span className="clinical-hint-text">{analyte.interpretation_hint}</span>
                  </div>
                  <div className="lab-result-data">
                    <span className="clinical-guide-tag">{analyte.guide}</span>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={idx}
                className={`lab-result-item ${isLow ? 'status-low' : isHigh ? 'status-high' : 'status-normal'} ${isSearching ? 'clickable' : ''} ${hasClinicalRules ? 'has-clinical' : ''}`}
                title={hasClinicalRules ? 'Click para ver clasificación clínica' : analyte.name}
                onClick={hasClinicalRules ? (e) => toggleFlip(cardKey, e) : undefined}
                style={hasClinicalRules ? { cursor: 'pointer' } : undefined}
              >
                <span className="lab-result-status">
                  {isLow && <FiTrendingDown />}
                  {isHigh && <FiTrendingUp />}
                  {isNormal && <FiCheckCircle />}
                  {isUnknown && <FiInfo />}
                </span>
                <div className="lab-result-header">
                  <span className="lab-result-name">{analyte.name}</span>
                  {isSearching && (
                    <span className="lab-result-study">{analyte.studyName}</span>
                  )}
                </div>
                <div className="lab-result-data">
                  <div className="lab-result-value">
                    <span className="value">{formatNumber(analyte.value)}</span>
                    <span className="unit">{analyte.unit}</span>
                  </div>
                  <div className="lab-result-range">
                    {percentage !== null ? (
                      <>
                        <ProgressBar
                          now={percentage}
                          variant={isNormal ? 'success' : isLow ? 'warning' : 'danger'}
                          className="lab-progress"
                        />
                        <div className="range-labels">
                          <span>{formatRangeValue(analyte.reference_min)}</span>
                          <span>{formatRangeValue(analyte.reference_max)}</span>
                        </div>
                      </>
                    ) : (
                      // Sin rango utilizable no se dibuja barra: una barra necesita dos
                      // extremos, y pintarla sobre el centinela es lo que marcaba en rojo
                      // un valor que el laboratorio dio por normal.
                      <div className="range-labels">
                        <span className="text-muted fst-italic">
                          Sin rango de referencia del laboratorio
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination Controls - Only show if multiple studies */}
      {totalStudies > 1 && (
        <div className="carousel-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => goToStudy(currentStudy - 1)}
            disabled={currentStudy === 0}
            className="carousel-btn"
          >
            <FiChevronLeft />
          </Button>

          <div className="carousel-dots">
            {labTests.map((test, idx) => (
              <button
                key={idx}
                className={`dot ${currentStudy === idx ? 'active' : ''}`}
                onClick={() => goToStudy(idx)}
                title={getStudyName(test)}
              />
            ))}
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => goToStudy(currentStudy + 1)}
            disabled={currentStudy === totalStudies - 1}
            className="carousel-btn"
          >
            <FiChevronRight />
          </Button>

          <span className="carousel-info">
            {currentStudy + 1} / {totalStudies}
          </span>
        </div>
      )}
    </div>
  );
};

// Helper functions for questionnaires
const getRiskColor = (riskLevel: string) => {
  if (riskLevel === 'high' || riskLevel === 'severe' || riskLevel === 'moderately_severe') return 'danger';
  if (riskLevel === 'medium' || riskLevel === 'moderate' || riskLevel === 'mild') return 'warning';
  return 'success';
};

const getRiskLabel = (riskLevel: string) => {
  const labels: Record<string, string> = {
    high: 'Alto',
    medium: 'Medio',
    low: 'Bajo',
    severe: 'Severo',
    moderately_severe: 'Moderadamente Severo',
    moderate: 'Moderado',
    mild: 'Leve',
    minimal: 'Mínimo',
    none: 'Ninguno',
  };
  return labels[riskLevel] || riskLevel;
};

const getTestLabel = (testType: string) => {
  const labels: Record<string, string> = {
    'HRA_CARDIOVASCULAR': 'Evaluación Cardiovascular',
    'HRA_DIABETES': 'Evaluación Diabetes',
    'HRA_GENERAL': 'Evaluación General',
    'PHQ9': 'Cuestionario PHQ-9 (Depresión)',
    'NOM035': 'Evaluación NOM-035',
    'GAD7': 'Cuestionario GAD-7 (Ansiedad)',
    'AUDIT': 'Cuestionario AUDIT',
  };
  return labels[testType] || testType.replace(/_/g, ' ');
};

// Questionnaire Carousel - Paginated by questionnaire
const QuestionnaireCarousel: React.FC<{ questionnaires: any[] }> = ({ questionnaires }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalQuestionnaires = questionnaires.length;

  const goToQuestionnaire = (index: number) => {
    if (index >= 0 && index < totalQuestionnaires) {
      setCurrentIndex(index);
    }
  };

  const currentTest = questionnaires[currentIndex];
  const testType = currentTest?.type || currentTest?.test_type || '';
  const score = Number(currentTest?.score) || 0;
  const maxScore = Number(currentTest?.max_score) || 100;
  const percentage = (score / maxScore) * 100;
  const riskLevel = currentTest?.risk_level || currentTest?.severity || 'low';

  return (
    <div className="questionnaire-carousel">
      {/* Questionnaire Content */}
      <div className="questionnaire-card">
        <div className="questionnaire-header">
          {totalQuestionnaires > 1 ? (
            <Dropdown className="study-dropdown">
              <Dropdown.Toggle variant="link" className="study-dropdown-toggle">
                <span className="study-name">{getTestLabel(testType)}</span>
                <FiChevronDown className="dropdown-icon" />
              </Dropdown.Toggle>
              <Dropdown.Menu className="study-dropdown-menu">
                {questionnaires.map((q, idx) => {
                  const qType = q.type || q.test_type || '';
                  const qRisk = q.risk_level || q.severity || 'low';
                  return (
                    <Dropdown.Item
                      key={idx}
                      active={currentIndex === idx}
                      onClick={() => goToQuestionnaire(idx)}
                      className="study-dropdown-item"
                    >
                      <span className="study-item-name">{getTestLabel(qType)}</span>
                      <Badge bg={getRiskColor(qRisk)} className="study-item-risk">
                        {getRiskLabel(qRisk)}
                      </Badge>
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          ) : (
            <h6>{getTestLabel(testType)}</h6>
          )}
          <Badge bg={getRiskColor(riskLevel)} className="risk-badge">
            {getRiskLabel(riskLevel)}
          </Badge>
        </div>

        <div className="questionnaire-score">
          <div className="score-circle">
            <svg viewBox="0 0 36 36" className="circular-chart">
              <path
                className="circle-bg"
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className={`circle-progress ${getRiskColor(riskLevel)}`}
                strokeDasharray={`${percentage}, 100`}
                d="M18 2.0845
                  a 15.9155 15.9155 0 0 1 0 31.831
                  a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <div className="score-text">
              <span className="score-value">{score}</span>
              <span className="score-max">/ {maxScore}</span>
            </div>
          </div>
        </div>

        {currentTest?.risk_factors && currentTest.risk_factors.length > 0 && (
          <div className="risk-factors">
            <small className="text-muted d-block mb-2">Factores de Riesgo:</small>
            <div className="d-flex flex-wrap gap-1">
              {currentTest.risk_factors.map((factor: string, idx: number) => (
                <Badge key={idx} bg="light" text="dark" className="factor-badge">
                  {factor.replace(/_/g, ' ')}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Pagination Controls - Only show if multiple questionnaires */}
      {totalQuestionnaires > 1 && (
        <div className="carousel-controls">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => goToQuestionnaire(currentIndex - 1)}
            disabled={currentIndex === 0}
            className="carousel-btn"
          >
            <FiChevronLeft />
          </Button>

          <div className="carousel-dots">
            {questionnaires.map((q, idx) => (
              <button
                key={idx}
                className={`dot ${currentIndex === idx ? 'active' : ''}`}
                onClick={() => goToQuestionnaire(idx)}
                title={getTestLabel(q.type || q.test_type || '')}
              />
            ))}
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => goToQuestionnaire(currentIndex + 1)}
            disabled={currentIndex === totalQuestionnaires - 1}
            className="carousel-btn"
          >
            <FiChevronRight />
          </Button>

          <span className="carousel-info">
            {currentIndex + 1} / {totalQuestionnaires}
          </span>
        </div>
      )}
    </div>
  );
};

// Interpretation Wizard Component
interface WizardProps {
  interpretationText: string;
  aiConfidence?: number;
  actionPlan?: {
    priority: string;
    summary: string;
    recommendations: Array<{
      title: string;
      description: string;
      timeframe?: string;
    }>;
    follow_up?: {
      suggested_tests?: string[];
      suggested_date?: string;
    };
  };
  activeStep: number;
  onStepChange: (step: number) => void;
  // Auditor info for signature
  status?: string;
  auditorId?: string;
  auditorName?: string;
  auditorEmail?: string;
  auditorPhotoUrl?: string;
  reviewedAt?: string;
  // Rejection info
  rejectedBy?: string;
  rejectedAt?: string;
  rejectorName?: string;
  rejectorEmail?: string;
  rejectorPhotoUrl?: string;
}

const InterpretationWizard: React.FC<WizardProps> = ({
  interpretationText,
  aiConfidence,
  actionPlan,
  activeStep,
  onStepChange,
  status,
  auditorId,
  auditorName,
  auditorEmail,
  auditorPhotoUrl,
  reviewedAt,
  rejectedBy,
  rejectedAt,
  rejectorName,
  rejectorEmail,
  rejectorPhotoUrl,
}) => {

  const steps = [
    { id: 'interpretation', label: 'Interpretación Médica', icon: <FiActivity /> },
    { id: 'action-plan', label: 'Plan de Acción', icon: <FiClipboard /> },
  ];

  const hasActionPlan = actionPlan && (actionPlan.summary || actionPlan.recommendations?.length > 0);

  const goToStep = (step: number) => {
    if (step >= 0 && step < steps.length) {
      onStepChange(step);
    }
  };

  const getPriorityInfo = (priority: string) => {
    const info: Record<string, { variant: string; label: string }> = {
      high: { variant: 'danger', label: 'Alta' },
      medium: { variant: 'warning', label: 'Media' },
      low: { variant: 'secondary', label: 'Baja' },
    };
    return info[priority] || info.low;
  };

  return (
    <div className="interpretation-panel">
      <Card className="wizard-card">
        {/* Wizard Header with Steps */}
        <Card.Header className="wizard-header">
          <div className="wizard-steps">
            {steps.map((step, idx) => (
              <button
                key={step.id}
                className={`wizard-step ${activeStep === idx ? 'active' : ''} ${idx < activeStep ? 'completed' : ''}`}
                onClick={() => goToStep(idx)}
                disabled={step.id === 'action-plan' && !hasActionPlan}
              >
                <span className="step-number">{idx + 1}</span>
                <span className="step-icon">{step.icon}</span>
                <span className="step-label">{step.label}</span>
                {idx < activeStep && <FiCheckCircle className="step-check" />}
              </button>
            ))}
          </div>
          <div className="wizard-progress">
            <div
              className="wizard-progress-bar"
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </Card.Header>

        {/* Wizard Content */}
        <Card.Body className="wizard-body">
          {/* Step 1: Interpretation */}
          {activeStep === 0 && (
            <div className="wizard-content interpretation-step">
              <div className="step-header">
                <h5>
                  <FiActivity className="me-2" />
                  Interpretación Médica
                </h5>
                {aiConfidence !== undefined && (
                  <Badge bg={aiConfidence >= 0.8 ? 'success' : aiConfidence >= 0.6 ? 'warning' : 'danger'}>
                    {Math.round(aiConfidence * 100)}% confianza
                  </Badge>
                )}
              </div>

              <div className="interpretation-content">
                {interpretationText ? (
                  <div className="prose">
                    <ReactMarkdown>{interpretationText}</ReactMarkdown>
                  </div>
                ) : (
                  <div className="empty-state">
                    <FiAlertCircle size={48} />
                    <p>No hay interpretación disponible</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Action Plan */}
          {activeStep === 1 && (
            <div className="wizard-content action-plan-step">
              <div className="step-header">
                <h5>
                  <FiClipboard className="me-2" />
                  Plan de Acción
                </h5>
                {actionPlan && (
                  <Badge bg={getPriorityInfo(actionPlan.priority).variant}>
                    Prioridad {getPriorityInfo(actionPlan.priority).label}
                  </Badge>
                )}
              </div>

              {hasActionPlan ? (
                <div className="action-plan-content">
                  {actionPlan.summary && (
                    <div className="plan-summary">
                      <p>{actionPlan.summary}</p>
                    </div>
                  )}

                  {actionPlan.recommendations && actionPlan.recommendations.length > 0 && (
                    <div className="recommendations-section">
                      <h6>Recomendaciones</h6>
                      <div className="recommendations-list">
                        {actionPlan.recommendations.map((rec, idx) => (
                          <div key={idx} className="recommendation-card">
                            <div className="rec-header">
                              <span className="rec-number">{idx + 1}</span>
                              <strong className="rec-title">{rec.title}</strong>
                            </div>
                            <p className="rec-description">{rec.description}</p>
                            {rec.timeframe && (
                              <Badge bg="light" text="dark" className="rec-timeframe">
                                <FiActivity size={12} className="me-1" />
                                {rec.timeframe}
                              </Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {actionPlan.follow_up && (
                    <div className="follow-up-section">
                      <h6>Seguimiento Recomendado</h6>
                      <div className="follow-up-content">
                        {actionPlan.follow_up.suggested_tests && actionPlan.follow_up.suggested_tests.length > 0 && (
                          <div className="follow-up-item">
                            <strong>Exámenes sugeridos:</strong>
                            <div className="suggested-tests">
                              {actionPlan.follow_up.suggested_tests.map((test, idx) => (
                                <Badge key={idx} bg="info" className="test-badge">
                                  {test}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {actionPlan.follow_up.suggested_date && (
                          <div className="follow-up-item">
                            <strong>Fecha sugerida:</strong>
                            <span className="suggested-date">{actionPlan.follow_up.suggested_date}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <FiClipboard size={48} />
                  <p>No hay plan de acción disponible</p>
                </div>
              )}
            </div>
          )}
        </Card.Body>

        {/* Auditor Signature Footer - shown when approved or rejected */}
        {(status === 'approved' || status === 'approved_with_edit') && auditorId && (
          <Card.Footer className="wizard-footer">
            <AuditorSignature
              auditorId={auditorId}
              auditorName={auditorName}
              auditorEmail={auditorEmail}
              auditorPhotoUrl={auditorPhotoUrl}
              reviewedAt={reviewedAt}
              status={status}
              size="md"
            />
          </Card.Footer>
        )}
        {status === 'rejected' && rejectedBy && (
          <Card.Footer className="wizard-footer">
            <AuditorSignature
              auditorId={rejectedBy}
              auditorName={rejectorName}
              auditorEmail={rejectorEmail}
              auditorPhotoUrl={rejectorPhotoUrl}
              reviewedAt={rejectedAt}
              status="rejected"
              size="md"
            />
          </Card.Footer>
        )}
      </Card>
    </div>
  );
};

// Categorías de feedback para regeneración
const REGENERATE_CATEGORIES = [
  { value: 'tone', label: 'Tono y lenguaje' },
  { value: 'clinical_accuracy', label: 'Precisión clínica' },
  { value: 'missing_info', label: 'Información faltante' },
  { value: 'action_plan', label: 'Plan de acción' },
  { value: 'format', label: 'Formato/estructura' },
  { value: 'other', label: 'Otro' },
];

// Motivos de rechazo de interpretación
const REJECTION_REASONS: { value: RejectionReason; label: string }[] = [
  { value: 'invalid_data', label: 'Datos inválidos' },
  { value: 'expired', label: 'Interpretación expirada' },
  { value: 'duplicate', label: 'Duplicada' },
  { value: 'other', label: 'Otro' },
];

// Motivos de edición de interpretación
const EDIT_REASONS = [
  { value: 'terminology', label: 'Ajuste de terminología médica' },
  { value: 'clinical_precision', label: 'Corrección de precisión clínica' },
  { value: 'missing_findings', label: 'Agregar hallazgos faltantes' },
  { value: 'remove_incorrect', label: 'Eliminar información incorrecta' },
  { value: 'patient_context', label: 'Contextualizar según historial del paciente' },
  { value: 'recommendations', label: 'Modificar recomendaciones' },
  { value: 'tone_adjustment', label: 'Ajuste de tono/lenguaje' },
  { value: 'other', label: 'Otro motivo' },
];

const InterpretationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [interpretation, setInterpretation] = useState<Interpretation | null>(null);

  // Audit logging - tracks auditor actions on this interpretation
  const auditLogger = useAuditLogger(id, interpretation?.patient_id, 'direct');
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [editedInterpretation, setEditedInterpretation] = useState('');
  const [modificationReason, setModificationReason] = useState('');
  const [auditorNotes, setAuditorNotes] = useState('');
  const [regenerateCategory, setRegenerateCategory] = useState('');
  const [regenerateDescription, setRegenerateDescription] = useState('');
  const [alertsCollapsed, setAlertsCollapsed] = useState(false);
  const [editViewMode, setEditViewMode] = useState<'preview' | 'edit'>('preview');
  const [wizardStep, setWizardStep] = useState(0);
  const [editedActionPlan, setEditedActionPlan] = useState<{
    summary: string;
    recommendations: Array<{ title: string; description: string; timeframe?: string }>;
    follow_up?: {
      suggested_tests: string[];
      suggested_date: string;
    };
  }>({ summary: '', recommendations: [] });
  const [currentRecommendationIdx, setCurrentRecommendationIdx] = useState(0);
  const [actionPlanSection, setActionPlanSection] = useState<'recommendations' | 'follow_up'>('recommendations');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsProvider, setTtsProvider] = useState<'browser' | 'polly'>('browser');
  const [sendingReport, setSendingReport] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [previewingReport, setPreviewingReport] = useState(false);
  const [showAuditTimeline, setShowAuditTimeline] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  // Whether this interpretation's company gets its reports by mail at all. `null` is
  // UNKNOWN -- either not asked yet or the lookup failed -- and unknown reads exactly
  // like today's screen. Only an explicit `delivers_by_email: false` changes a word.
  const [deliveryPolicy, setDeliveryPolicy] = useState<DeliveryPolicy | null>(null);
  // Approving mails the PDF to the patient immediately and cannot be undone, so it
  // goes through an explicit confirmation. `withEdit` remembers which path asked.
  const [confirmSendWithEdit, setConfirmSendWithEdit] = useState<boolean | null>(null);
  // The second gate, and ONLY for a case the AI read without age or sex (E99-F14).
  // Same shape as `confirmSendWithEdit` because it takes over the same decision:
  // `null` is "not asked", and the boolean carries which path asked, so the approval
  // that finally runs is the one the auditor started. A complete case never sets it.
  const [confirmBlindWithEdit, setConfirmBlindWithEdit] = useState<boolean | null>(null);
  // Deliberately not a plain button-click: the auditor has to state that they know
  // what was missing. Reset every time the dialog opens -- an acknowledgement that
  // survives from one record to the next is not an acknowledgement.
  const [blindAcknowledged, setBlindAcknowledged] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState<RejectionReason | ''>('');
  const [rejectionComment, setRejectionComment] = useState('');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const editorNodeRef = useRef<HTMLDivElement | null>(null);
  const editorInitializedRef = useRef(false);
  const ttsSectionRef = useRef<TTSSection>('interpretation');

  // Fetch TTS config on mount
  useEffect(() => {
    const fetchTtsConfig = async () => {
      try {
        const config = await ttsApi.getConfig();
        setTtsProvider(config.tts_provider);
      } catch {
        // Default to browser
        setTtsProvider('browser');
      }
    };
    fetchTtsConfig();
  }, []);

  // Clean markdown text
  const cleanMarkdownText = (text: string): string => {
    return text
      .replace(/#{1,6}\s?/g, '') // Eliminar headers
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Eliminar bold
      .replace(/\*([^*]+)\*/g, '$1') // Eliminar italic
      .replace(/- /g, ', ') // Convertir bullets
      .replace(/\n+/g, '. ') // Convertir saltos de línea
      .replace(/[>|`]/g, '') // Eliminar caracteres especiales
      .trim();
  };

  // Browser-based TTS (Web Speech API)
  const speakWithBrowser = useCallback((text: string) => {
    if (!('speechSynthesis' in window)) {
      toast.warning('TTS no disponible', 'Tu navegador no soporta la función de lectura de texto');
      return;
    }

    const cleanText = cleanMarkdownText(text);
    const utterance = new SpeechSynthesisUtterance(cleanText);
    utterance.lang = 'es-ES';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    const voices = window.speechSynthesis.getVoices();
    const spanishVoice = voices.find(v => v.lang.startsWith('es'));
    if (spanishVoice) {
      utterance.voice = spanishVoice;
    }

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      auditLogger.logTTSStop(true); // true = completed naturally
    };
    utterance.onerror = () => {
      setIsSpeaking(false);
      auditLogger.logTTSStop(false); // false = error/interrupted
    };

    window.speechSynthesis.speak(utterance);
  }, [toast, auditLogger]);

  // Polly-based TTS
  const speakWithPolly = useCallback(async (text: string) => {
    const cleanText = cleanMarkdownText(text);

    try {
      setIsSpeaking(true);
      // Pass interpretation_id and patient_id for cost tracking
      const response = await ttsApi.synthesize(
        cleanText,
        interpretation?.interpretation_id,
        interpretation?.patient_id
      );

      // Create audio from base64
      const audioData = `data:${response.content_type};base64,${response.audio}`;
      const audio = new Audio(audioData);
      audioRef.current = audio;

      audio.onended = () => {
        setIsSpeaking(false);
        auditLogger.logTTSStop(true); // true = completed naturally
      };
      audio.onerror = () => {
        setIsSpeaking(false);
        auditLogger.logTTSStop(false); // false = error/interrupted
        toast.error('Error de audio', 'No se pudo reproducir el audio');
      };

      await audio.play();
    } catch (err) {
      setIsSpeaking(false);
      auditLogger.logTTSStop(false); // false = error
      toast.error('Error de TTS', 'No se pudo sintetizar el audio');
    }
  }, [toast, auditLogger, interpretation?.interpretation_id, interpretation?.patient_id]);

  // Stop speaking
  const stopSpeaking = useCallback(() => {
    if (ttsProvider === 'browser') {
      window.speechSynthesis.cancel();
    } else if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsSpeaking(false);
  }, [ttsProvider]);

  // Main TTS handler
  const handleTextToSpeech = useCallback((text: string, section: TTSSection = 'interpretation') => {
    if (isSpeaking) {
      // Stopping TTS - log with completion status
      auditLogger.logTTSStop(false); // false = manually stopped, not completed
      stopSpeaking();
      return;
    }

    // Starting TTS - log start event
    ttsSectionRef.current = section;
    auditLogger.logTTSStart(section, ttsProvider);

    if (ttsProvider === 'polly') {
      speakWithPolly(text);
    } else {
      speakWithBrowser(text);
    }
  }, [isSpeaking, ttsProvider, speakWithPolly, speakWithBrowser, stopSpeaking, auditLogger]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Callback ref para inicializar el editor cuando el elemento se monta
  const setEditorRef = useCallback((node: HTMLDivElement | null) => {
    editorNodeRef.current = node;
    if (node && showEditModal && editViewMode === 'preview' && !editorInitializedRef.current) {
      const html = marked.parse(editedInterpretation || '') as string;
      node.innerHTML = html;
      editorInitializedRef.current = true;
    }
  }, [showEditModal, editViewMode, editedInterpretation]);

  // La bandera significa "el panel que está montado AHORA ya se pobló", así que hay
  // que bajarla cada vez que ese panel deja de existir: al cerrar el modal, al pasar a
  // "Editar Código" y al pasar al paso del plan de acción. React monta un <div> nuevo y
  // vacío al volver, y con la bandera en alto el panel se quedaba en blanco -- y lo que
  // la auditora escribiera ahí se guardaba como un borrador vacío.
  //
  // Se baja al SALIR, no al volver: al volver el ref ya se ha enganchado (y ha poblado)
  // antes de que corran los efectos, y dejarla baja haría que cada tecla -- que cambia
  // la identidad del callback del ref -- repoblara el panel encima de lo escrito.
  const editorPaneMounted =
    showEditModal && wizardStep === 0 && editViewMode === 'preview';
  useEffect(() => {
    if (!editorPaneMounted) {
      editorInitializedRef.current = false;
    }
  }, [editorPaneMounted]);

  // Funciones para aplicar formato WYSIWYG
  const applyFormat = useCallback((command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorNodeRef.current?.focus();
  }, []);

  const handleEditorInput = useCallback(() => {
    if (editorNodeRef.current) {
      // Serializar el HTML del panel de vuelta a markdown, que es el formato que se
      // guarda. `innerText` devolvía texto plano, así que una sola tecla en cualquier
      // parte aplanaba el documento entero -- y de ahí pasaba al borrador y al PDF.
      setEditedInterpretation(htmlToMarkdown(editorNodeRef.current.innerHTML));
    }
  }, []);

  useEffect(() => {
    const fetchInterpretation = async () => {
      if (!id) return;

      // Use demo data if enabled
      if (USE_DEMO_DATA) {
        setInterpretation(DEMO_INTERPRETATION);
        // A saved draft is where the auditor left off, so it is what the editor must
        // open with -- reopening the AI text would silently throw their work away.
        setEditedInterpretation(
          DEMO_INTERPRETATION.draft_interpretation || DEMO_INTERPRETATION.ai_interpretation || ''
        );
        if (DEMO_INTERPRETATION.action_plan) {
          setEditedActionPlan({
            summary: DEMO_INTERPRETATION.action_plan.summary || '',
            recommendations: DEMO_INTERPRETATION.action_plan.recommendations || [],
            follow_up: {
              suggested_tests: DEMO_INTERPRETATION.action_plan.follow_up?.suggested_tests || [],
              suggested_date: DEMO_INTERPRETATION.action_plan.follow_up?.suggested_date || '',
            },
          });
        }
        setLoading(false);
        return;
      }

      try {
        const data = await interpretationsApi.getById(id);
        setInterpretation(data);
        // Same precedence as the demo branch above: draft first, AI text second.
        setEditedInterpretation(data.draft_interpretation || data.ai_interpretation || '');
        if (data.action_plan) {
          setEditedActionPlan({
            summary: data.action_plan.summary || '',
            recommendations: data.action_plan.recommendations || [],
            follow_up: {
              suggested_tests: data.action_plan.follow_up?.suggested_tests || [],
              suggested_date: data.action_plan.follow_up?.suggested_date || '',
            },
          });
        }
      } catch (error) {
        console.error('Error fetching interpretation:', error);
        toast.error('Error al cargar', 'No se pudo cargar la interpretación');
      } finally {
        setLoading(false);
      }
    };

    /**
     * The company's delivery rule, in its OWN try/catch and its own call.
     *
     * Separate from the fetch above for two reasons. A failure here means the policy
     * is UNKNOWN, never "does not deliver": left as `null` the screen keeps today's
     * wording, which is the safe direction -- announcing "no mail will go out"
     * because a lookup broke would be the same lie this feature exists to end. And a
     * rejection here must not take the interpretation down with it, nor the reverse.
     *
     * Fired once per `id`, never on an interval: this screen does not poll.
     */
    const fetchDeliveryPolicy = async () => {
      if (!id || USE_DEMO_DATA) return;

      try {
        const policy = await interpretationsApi.getDeliveryPolicy(id);
        setDeliveryPolicy(policy ?? null);
      } catch (error) {
        console.error('Error fetching delivery policy:', error);
        setDeliveryPolicy(null);
      }
    };

    fetchInterpretation();
    fetchDeliveryPolicy();
  }, [id, toast]);

  const handleApprove = async (withEdit: boolean = false) => {
    if (!id || !interpretation) return;
    setApproving(true);

    try {
      const data: any = {
        auditor_notes: auditorNotes || undefined,
      };

      if (withEdit) {
        data.final_interpretation = editedInterpretation;
        data.modification_reason = modificationReason;
      }

      // Note: auditor_id, auditor_name, auditor_email are extracted from JWT in backend
      const updated = await interpretationsApi.approve(id, data);
      setInterpretation(updated);
      setShowEditModal(false);

      // Log audit event
      if (withEdit) {
        const originalLength = interpretation.ai_interpretation?.length || 0;
        const editedLength = editedInterpretation.length;
        const charsChanged = Math.abs(editedLength - originalLength);
        auditLogger.logApproveWithEdit(modificationReason, charsChanged);
      } else {
        auditLogger.logApprove();
      }

      toast.success(
        'Interpretación aprobada',
        withEdit ? 'Los cambios han sido guardados correctamente' : 'La interpretación ha sido aprobada'
      );
    } catch (error) {
      console.error('Error approving interpretation:', error);
      toast.error('Error al aprobar', 'No se pudo aprobar la interpretación. Por favor intenta de nuevo.');
    } finally {
      setApproving(false);
    }
  };

  /**
   * Saves the auditor's work in progress and closes the editor. Nothing else.
   *
   * No approval, no confirmation dialog, no PDF, no email, and no `modificationReason`
   * requirement -- that field belongs to "Guardar y enviar", which is a different
   * decision. This is the button the doctor was looking for on 2026-08-11.
   *
   * On failure the modal stays open with the text untouched: losing the text to a
   * network error would be WORSE than not having this feature, because the auditor
   * would walk away believing their work was saved.
   */
  const handleSaveDraft = async () => {
    if (!id || !interpretation) return;
    setSavingDraft(true);

    try {
      const saved = await interpretationsApi.saveDraft(id, editedInterpretation);
      setInterpretation({
        ...interpretation,
        draft_interpretation: editedInterpretation,
        draft_saved_at: saved.draft_saved_at,
        draft_saved_by: saved.draft_saved_by,
        draft_saved_by_name: saved.draft_saved_by_name,
      });
      setShowEditModal(false);
      setEditViewMode('preview');
      toast.success(
        'Borrador guardado',
        'Tu texto quedó guardado. No se aprobó nada ni se envió nada al paciente.'
      );
    } catch (error: any) {
      console.error('Error saving draft:', error);
      const errorMsg = error.response?.data?.error || 'No se pudo guardar el borrador';
      // Deliberately NOT closing the modal and NOT clearing editedInterpretation.
      toast.error('Error al guardar el borrador', `${errorMsg}. Tu texto sigue aquí.`);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleRegenerate = async () => {
    if (!id) return;

    const categoryLabel = REGENERATE_CATEGORIES.find(
      c => c.value === regenerateCategory
    )?.label;

    const additionalContext = `[CATEGORÍA: ${categoryLabel}]\n${regenerateDescription}`;

    // Log regeneration request
    auditLogger.logRegenerate(regenerateCategory, regenerateDescription);

    setRegenerating(true);
    setShowRegenerateModal(false);

    // `regenerate` sólo ARRANCA la máquina de estados; el texto tarda ~80 s en escribirse.
    // Hasta hoy se consultaba la interpretación en el instante siguiente —devolviendo el
    // texto viejo y el estado `processing`— y aun así se anunciaba «lista para revisión».
    // El médico leía «ya acabó», veía el texto de antes y sólo una recarga forzada se lo
    // mostraba: un aviso que miente vale menos que no avisar.
    const countBefore = Number(interpretation?.regeneration_count ?? 0);

    try {
      await interpretationsApi.regenerate(id, additionalContext);
      setRegenerateCategory('');
      setRegenerateDescription('');
      toast.info('Regeneración iniciada', 'La nueva interpretación se está generando, puede tardar un par de minutos...');

      const listo = await esperarRegeneracion(id, countBefore, setInterpretation, {
        obtener: interpretationsApi.getById,
      });

      if (listo === 'ready') {
        toast.success('Interpretación regenerada', 'La nueva interpretación está lista para revisión');
      } else if (listo === 'failed') {
        toast.error('La regeneración falló', 'La interpretación quedó sin generar. Avisa a soporte antes de volver a intentarlo.');
      } else {
        toast.warning('Sigue en proceso', 'Está tardando más de lo normal. Vuelve a abrir el paciente en unos minutos para ver el texto nuevo.');
      }
    } catch (error) {
      console.error('Error regenerating interpretation:', error);
      toast.error('Error al regenerar', 'No se pudo regenerar la interpretación. Por favor intenta de nuevo.');
    } finally {
      setRegenerating(false);
    }
  };

  const handleReject = async () => {
    if (!id || !rejectionReason) return;

    // Validate comment for "other" reason
    if (rejectionReason === 'other' && !rejectionComment.trim()) {
      toast.error('Comentario requerido', 'Debe proporcionar un comentario cuando selecciona "Otro"');
      return;
    }

    setRejecting(true);
    setShowRejectModal(false);

    try {
      await interpretationsApi.reject(
        id,
        rejectionReason,
        rejectionComment.trim() || undefined
      );
      toast.success('Interpretación rechazada', 'La interpretación ha sido marcada como rechazada');
      // Refresh interpretation
      const data = await interpretationsApi.getById(id);
      setInterpretation(data);
      // Reset form
      setRejectionReason('');
      setRejectionComment('');
    } catch (error: any) {
      console.error('Error rejecting interpretation:', error);
      const errorMsg = error.response?.data?.error || 'No se pudo rechazar la interpretación';
      toast.error('Error al rechazar', errorMsg);
    } finally {
      setRejecting(false);
    }
  };

  // Report handling functions
  const handleSendReport = async () => {
    if (!id) return;
    setSendingReport(true);

    try {
      const result = await interpretationsApi.sendReport(id);

      // Outside production the backend declines the send and answers "skipped".
      // Nothing failed, so this is not an error -- but saying "enviado" would put a
      // delivery in the auditor's head that never happened.
      if (result.status === 'skipped') {
        toast.info(
          'Envío omitido',
          result.message ||
            'El envío de reportes está deshabilitado en este ambiente. No se envió nada al paciente.'
        );
        return;
      }

      toast.success('Reporte enviado', 'El reporte ha sido enviado al paciente por correo electrónico');
      // Refresh interpretation to get updated report_status
      const data = await interpretationsApi.getById(id);
      setInterpretation(data);
    } catch (error: any) {
      console.error('Error sending report:', error);
      const errorMsg = error.response?.data?.error || 'No se pudo enviar el reporte';
      toast.error('Error al enviar reporte', errorMsg);
    } finally {
      setSendingReport(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!id) return;
    setDownloadingReport(true);

    try {
      const result = await interpretationsApi.getReportUrl(id);

      // 202: the render outlasted the gateway and Azure is still working. Opening
      // the tab anyway would land on about:blank with no explanation.
      if (!result.url) {
        toast.info(
          'Generando el reporte',
          result.message ||
            'El reporte se está generando. Vuelve a intentarlo en unos segundos.'
        );
        return;
      }

      // Open the signed URL in a new tab
      window.open(result.url, '_blank');
      toast.success('Descargando reporte', 'El PDF se está descargando...');
    } catch (error: any) {
      console.error('Error downloading report:', error);
      const errorMsg = error.response?.data?.error || 'No se pudo obtener el reporte';
      toast.error('Error al descargar reporte', errorMsg);
    } finally {
      setDownloadingReport(false);
    }
  };

  /**
   * Opens the deliverable the patient would receive, without sending it.
   *
   * Approving is what mails the PDF, so without this the recipient is the first
   * person to ever see it. The URL is rendered on demand and read-only; nothing
   * is delivered by looking.
   */
  /**
   * Opens the deliverable the patient would receive, without sending it.
   *
   * `force` re-renders instead of reading the cache -- what you want after
   * editing the text. A cold render pulls three months of results, three years of
   * biomarker history and a health score, which can outlast the gateway; the
   * backend answers 202 in that case and Azure keeps working, so a retry lands on
   * a warm cache.
   */
  const handlePreviewReport = async (force = false) => {
    if (!id) return;

    // Open the tab synchronously, inside the click's gesture context. Calling
    // window.open after the await loses that context and pop-up blockers reject
    // it, which reads to the auditor as a button that does nothing.
    const previewTab = window.open('', '_blank');
    if (!previewTab) {
      toast.error(
        'Vista previa bloqueada',
        'Permite las ventanas emergentes de este sitio para ver el reporte.'
      );
      return;
    }

    setPreviewingReport(true);

    try {
      const result = await interpretationsApi.getReportUrl(id, true, force);

      if (!result.url) {
        previewTab.close();
        toast.info(
          'Generando la vista previa',
          result.message ||
            'El reporte se está generando. Vuelve a intentarlo en unos segundos.'
        );
        return;
      }

      // Audited server-side as REPORT_PREVIEWED, where it cannot be skipped.
      previewTab.location.href = result.url;
    } catch (error: any) {
      previewTab.close();
      console.error('Error previewing report:', error);
      const errorMsg = error.response?.data?.error || 'No se pudo generar la vista previa';
      toast.error('Error en la vista previa', errorMsg);
    } finally {
      setPreviewingReport(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Cargando interpretación..." />;
  }

  if (!interpretation) {
    return (
      <div className="empty-state p-5 text-center">
        <h4 className="text-muted">No se encontró la interpretación solicitada</h4>
        <Button variant="outline-primary" onClick={() => navigate('/interpretations')} className="mt-3">
          Volver a la lista
        </Button>
      </div>
    );
  }

  const { raw_data, ai_interpretation, ai_confidence, ai_flags, action_plan } = interpretation;
  const patient_info = raw_data?.patient_info;

  // What the AI did not have. Empty on a complete case, and everything downstream
  // of this gate is keyed off `.length` so that a complete case takes exactly the
  // path it took before this feature existed -- same screen, same number of clicks.
  const blindDemographics = missingDemographics(patient_info);
  // Named, never a generic "faltan datos del paciente": the auditor has to know
  // which field to go and look at. Empty string on a complete case -- unreachable,
  // because nothing that reads it renders unless the list is non-empty.
  const blindPhrase = blindDemographics.join(' ni ');

  // Get tests from raw_data.tests, or fallback to building from group_interpretations
  // This handles hierarchical interpretations that were created before _build_tests_from_groups
  let tests = raw_data?.tests || [];
  if (tests.length === 0 && raw_data?.group_interpretations) {
    tests = buildTestsFromGroups(raw_data.group_interpretations);
  }

  const isPendingReview = interpretation.status === 'pending_review';
  const isApproved = interpretation.status === 'approved' || interpretation.status === 'approved_with_edit';

  // Some companies agreed with Previta that their reports do NOT reach employees by
  // mail: Previta downloads them and uploads them to the company's Drive. Approving
  // such a record mails nothing, so every surface that promises a mail has to stop
  // promising it -- BEFORE the auditor approves, which is the whole point.
  //
  // Only an explicit `false` flips it. `null` (not asked yet, or the lookup failed)
  // and `true` both leave today's screen untouched, word for word.
  const skipsEmailDelivery = deliveryPolicy?.delivers_by_email === false;
  // The `{empresa}` substitution and the backup the spec names for a blank company.
  const companyLabel = deliveryPolicy?.company_name || 'la empresa de este paciente';
  const noEmailSendHint = `${companyLabel} no recibe los reportes por correo. Descarga el PDF y súbelo al Drive de la empresa.`;
  // The provenance line keeps its three branches and its date; only the verb moves.
  // "Se enviará" would be plainly false when nothing is mailed, and a dialog that
  // contradicts itself is worse than one that says nothing.
  const textSourceVerb = skipsEmailDelivery ? 'El reporte llevará' : 'Se enviará';

  // Get interpretation text.
  //
  // THE single source for everything this screen presents as "the interpretation":
  // the rendered body and the text TTS reads both come from here, so they cannot
  // disagree. Do not duplicate this precedence anywhere else.
  //
  // Order matters. `final_interpretation` first (an approved record shows what was
  // actually delivered; by then the backend has already dropped the draft), the draft
  // second, the AI text last. Showing the AI text while a draft exists would mean the
  // auditor reads one text and the "Aprobar y enviar" button sends another -- the
  // same family of mismatch as the incident this feature answers.
  const interpretationText =
    interpretation.final_interpretation ||
    interpretation.draft_interpretation ||
    ai_interpretation ||
    '';

  // Generate action plan text for TTS
  const getActionPlanText = () => {
    if (!action_plan) return '';

    const parts: string[] = [];

    if (action_plan.summary) {
      parts.push(`Plan de Acción. ${action_plan.summary}`);
    }

    if (action_plan.recommendations && action_plan.recommendations.length > 0) {
      parts.push('Recomendaciones.');
      action_plan.recommendations.forEach((rec, idx) => {
        const recText = `${idx + 1}. ${rec.title}. ${rec.description}${rec.timeframe ? `. Plazo: ${rec.timeframe}` : ''}`;
        parts.push(recText);
      });
    }

    if (action_plan.follow_up) {
      if (action_plan.follow_up.suggested_date) {
        parts.push(`Seguimiento recomendado: ${action_plan.follow_up.suggested_date}.`);
      }
      if (action_plan.follow_up.suggested_tests && action_plan.follow_up.suggested_tests.length > 0) {
        parts.push(`Exámenes sugeridos: ${action_plan.follow_up.suggested_tests.join(', ')}.`);
      }
    }

    return parts.join(' ');
  };

  // Get text to read based on current wizard step
  const getTextToRead = () => {
    if (wizardStep === 1) {
      return getActionPlanText();
    }
    return interpretationText;
  };

  // Separate tests by type
  const labTests = tests.filter((t: any) => (t.type || t.test_type) === 'LAB_RESULTS');
  const questionnaireTests = tests.filter((t: any) => {
    const type = t.type || t.test_type;
    return type !== 'LAB_RESULTS';
  });

  // Get GAB studies data
  const gabStudiesData = raw_data?.gab_studies;
  const gabStudiesList = interpretation.gab_studies;
  const hasGabStudies = !!(gabStudiesData?.studies?.length || gabStudiesList?.length);

  const hasLabData = labTests.length > 0 || questionnaireTests.length > 0;

  // Determine order type. The DATA decides before any flag does: a record written by the
  // hierarchical route carried no `order_type` in raw_data, so a patient with an EKG and an
  // audiometry was read as lab_only and the Gabinete tab simply never appeared — studies
  // present, invisible, no error (Grupo Modelo, 2026-10-28). A declared flag is only used
  // when there is nothing to infer from.
  const declaredOrderType: OrderType | undefined =
    interpretation.order_type || raw_data?.order_type;
  const orderType: OrderType = hasGabStudies
    ? (hasLabData ? 'mixed' : 'gab_only')
    : (declaredOrderType || 'lab_only');
  const isMixedOrder = orderType === 'mixed';
  const isGabOnlyOrder = orderType === 'gab_only';

  return (
    <div className="interpretation-detail-v2">
      {/* Compact Header */}
      <div className="detail-header">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="link"
            className="p-0 text-muted btn-back"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft />
          </Button>
          <div>
            <h4 className="mb-0">{patient_info?.name || interpretation.patient_id}</h4>
            <div className="header-meta">
              <span data-testid="detail-patient-age">{ageLabel(patient_info?.age)}</span>
              <span className="separator">•</span>
              <span data-testid="detail-patient-sex">{sexLabel(patient_info?.sex)}</span>
              <span className="separator">•</span>
              <span>{safeFormatDate(interpretation.created_at, 'dd MMM yyyy')}</span>
              <span className="separator">•</span>
              <StatusBadge status={interpretation.status || 'pending_review'} />
            </div>
            {/* Third line: the campaign, from the same shared resolution the list's
                company cell uses. With no campaign the element is NOT rendered at
                all -- no empty div, no `&nbsp;`, no dash, no reserved gap -- which
                is the majority case today. It never falls back to
                `source_campaigns` or to "unknown". */}
            {(() => {
              const campaign = campaignLine(interpretation);
              return campaign && (
                <div data-testid="detail-campaign-line" className="header-campaign">
                  {campaign}
                </div>
              );
            })()}
          </div>
        </div>

        <div className="header-actions">
          {isPendingReview && (
            <>
              {/* Botón de lectura - solo visible cuando está pendiente de revisión */}
              <Button
                variant={isSpeaking ? 'warning' : 'outline-secondary'}
                size="sm"
                onClick={() => handleTextToSpeech(
                  getTextToRead(),
                  wizardStep === 1 ? 'action_plan' : 'interpretation'
                )}
                title={isSpeaking ? 'Detener lectura' : (wizardStep === 1 ? 'Leer plan de acción' : 'Leer interpretación')}
              >
                {isSpeaking ? <FiVolumeX /> : <FiVolume2 />}
                <span className="d-none d-md-inline ms-2">{isSpeaking ? 'Detener' : 'Leer'}</span>
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowRegenerateModal(true)}
                disabled={regenerating}
              >
                <FiRefreshCw className={regenerating ? 'spin' : ''} />
                <span className="d-none d-md-inline ms-2">Regenerar</span>
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  auditLogger.logEditStart();
                  setShowEditModal(true);
                }}
              >
                <FiEdit3 />
                <span className="d-none d-md-inline ms-2">Editar</span>
              </Button>
              {/* Closes the inspect-and-adjust group: the last thing an auditor
                  sees before deciding. Approving mails the PDF to the patient, so
                  this is the only chance to read what they will receive.

                  Split button because regenerating is the rare case -- only needed
                  after editing the text -- and a cold render is slow enough that it
                  should not be the default. */}
              <Dropdown as={ButtonGroup}>
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => handlePreviewReport(false)}
                  disabled={previewingReport}
                  title="Ver el PDF que recibiría el paciente. No lo envía."
                >
                  {previewingReport ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FiEye />
                  )}
                  <span className="d-none d-md-inline ms-2">Vista previa</span>
                </Button>
                <Dropdown.Toggle
                  split
                  variant="outline-info"
                  size="sm"
                  disabled={previewingReport}
                  id="preview-options"
                  aria-label="Más opciones de vista previa"
                />
                <Dropdown.Menu align="end">
                  <Dropdown.Item onClick={() => handlePreviewReport(true)}>
                    <FiRefreshCw className="me-2" />
                    Regenerar vista previa
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.ItemText className="small text-muted" style={{ maxWidth: 260 }}>
                    Úsalo si editaste el texto. Vuelve a construir el PDF en vez de
                    reusar el último generado, y tarda más.
                  </Dropdown.ItemText>
                </Dropdown.Menu>
              </Dropdown>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setShowRejectModal(true)}
                disabled={rejecting}
              >
                {rejecting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <FiXCircle />
                )}
                <span className="d-none d-md-inline ms-2">Rechazar</span>
              </Button>
              {/* "y enviar" is not decoration: approving mails the PDF to the
                  patient with no further step, and the previous label said nothing
                  about it. When the company is on Drive delivery no mail goes out at
                  all, and the label has to stop promising one -- this is the surface
                  the auditor reads if they never open the dialog. */}
              <Button
                variant="success"
                size="sm"
                onClick={() => setConfirmSendWithEdit(false)}
                disabled={approving}
                className="btn-approve"
              >
                {approving ? (
                  <>
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2">Aprobando...</span>
                  </>
                ) : (
                  <>
                    <FiCheck />
                    <span className="ms-2">
                      {skipsEmailDelivery ? 'Aprobar sin enviar' : 'Aprobar y enviar'}
                    </span>
                  </>
                )}
              </Button>
            </>
          )}

          {/* Report buttons for approved interpretations */}
          {isApproved && (
            <>
              {user?.roles.includes('reapertura_autorizada') && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowReopenModal(true)}
                >
                  <FiEdit3 />
                  <span className="d-none d-md-inline ms-2">Editar</span>
                </Button>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleDownloadReport}
                disabled={downloadingReport}
                title="Descargar reporte PDF"
              >
                {downloadingReport ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <FiDownload />
                )}
                <span className="ms-2 d-none d-md-inline">Descargar</span>
              </Button>
              {/* Disabled, not hidden. The backend declines the send on its own, so
                  this control is not the guarantee -- it is the explanation. Hiding
                  it would leave an auditor who expected a send with a screen that
                  looks broken instead of one that says what the arrangement is. */}
              <Button
                variant={
                  skipsEmailDelivery ? 'outline-secondary' :
                  interpretation.report_status === 'sent' ? 'outline-success' :
                  interpretation.report_status === 'failed' ? 'outline-warning' : 'outline-primary'
                }
                size="sm"
                onClick={handleSendReport}
                disabled={sendingReport || skipsEmailDelivery}
                title={
                  skipsEmailDelivery
                    ? noEmailSendHint
                    : interpretation.report_status === 'sent'
                    ? `Reenviar reporte (enviado: ${safeFormatDate(interpretation.report_sent_at, 'PPpp')})`
                    : interpretation.report_status === 'failed'
                    ? 'Reintentar envío de reporte'
                    : 'Enviar reporte al paciente'
                }
                // The visible label is one word wide and hidden below `md`; the
                // reason has to reach a screen reader too, not just a hover.
                aria-label={skipsEmailDelivery ? noEmailSendHint : undefined}
              >
                {sendingReport ? (
                  <Spinner animation="border" size="sm" />
                ) : skipsEmailDelivery ? (
                  <FiDownload />
                ) : (
                  <FiMail />
                )}
                <span className="ms-2 d-none d-md-inline">
                  {skipsEmailDelivery ? 'Sin envío' :
                   interpretation.report_status === 'sent' ? 'Reenviar' :
                   interpretation.report_status === 'failed' ? 'Reintentar' : 'Enviar'}
                </span>
              </Button>
            </>
          )}

          {/* Audit Timeline Button - always visible */}
          <AuditTimelineButton onClick={() => setShowAuditTimeline(true)} />
        </div>
      </div>

      {/* Draft indicator. Without it the body silently shows different text than the
          record was generated with, and the auditor has no way to tell that what they
          are reading is their own unsent edit. */}
      {interpretation.draft_interpretation && (
        <div className="draft-banner alert alert-info d-flex align-items-center gap-2 py-2 px-3 mb-3">
          <FiEdit3 className="flex-shrink-0" />
          <div className="small">
            <strong>Borrador sin enviar</strong>
            {' · '}
            guardado por {interpretation.draft_saved_by_name || 'un auditor'} el{' '}
            {safeFormatDate(interpretation.draft_saved_at, "d 'de' MMMM 'a las' HH:mm")}
            {'. '}
            Estás viendo este texto, no el original de la IA. Todavía no se ha enviado
            nada al paciente.
          </div>
        </div>
      )}

      <div className="detail-content">
        {/* Left Panel - Test Data with Carousel */}
        <div className="tests-panel">
          {/* Confidence Indicator */}
          {ai_confidence !== undefined && (
            <div className="confidence-card">
              <div className="confidence-header">
                <span>Confianza IA</span>
                <span className="confidence-value">{Math.round(ai_confidence * 100)}%</span>
              </div>
              <ProgressBar
                now={ai_confidence * 100}
                variant={ai_confidence >= 0.8 ? 'success' : ai_confidence >= 0.6 ? 'warning' : 'danger'}
              />
            </div>
          )}

          {/* Alerts - Collapsible */}
          {ai_flags && ai_flags.length > 0 && (
            <div className={`alerts-card ${alertsCollapsed ? 'collapsed' : ''}`}>
              <button
                className="alerts-header"
                onClick={() => setAlertsCollapsed(!alertsCollapsed)}
              >
                <FiAlertCircle />
                <span>Alertas ({ai_flags.length})</span>
                <span className="collapse-icon">
                  {alertsCollapsed ? <FiChevronDown /> : <FiChevronUp />}
                </span>
              </button>
              {!alertsCollapsed && (
                <ul className="alerts-list">
                  {ai_flags.map((flag, idx) => (
                    <li key={idx}>{flag}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Mixed Order: Tabs for LAB and GAB */}
          {isMixedOrder && hasLabData && hasGabStudies && (
            <Tab.Container defaultActiveKey="lab">
              <Nav variant="tabs" className="mb-3 studies-tabs">
                <Nav.Item>
                  <Nav.Link eventKey="lab" className="d-flex align-items-center gap-2">
                    <FiActivity />
                    Laboratorio
                    <Badge bg="secondary" pill className="ms-1">
                      {labTests.length + questionnaireTests.length}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="gab" className="d-flex align-items-center gap-2">
                    <FiImage />
                    Gabinete
                    <Badge bg="secondary" pill className="ms-1">
                      {gabStudiesData?.studies_count || gabStudiesList?.length || 0}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              <Tab.Content>
                <Tab.Pane eventKey="lab">
                  {/* Lab Results Carousel */}
                  {labTests.length > 0 && (
                    <div className="test-section">
                      <h6 className="section-title">
                        <FiActivity />
                        Estudios de Laboratorio ({labTests.length})
                      </h6>
                      <LabStudyCarousel labTests={labTests} />
                    </div>
                  )}
                  {/* Questionnaires Carousel */}
                  {questionnaireTests.length > 0 && (
                    <div className="test-section">
                      <h6 className="section-title">
                        <FiClipboard />
                        Evaluaciones ({questionnaireTests.length})
                      </h6>
                      <QuestionnaireCarousel questionnaires={questionnaireTests} />
                    </div>
                  )}
                </Tab.Pane>
                <Tab.Pane eventKey="gab">
                  <GabStudiesPanel
                    interpretationId={interpretation.interpretation_id}
                    gabStudies={gabStudiesData}
                    gabStudiesList={gabStudiesList}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          )}

          {/* LAB Only Order (original behavior) */}
          {!isMixedOrder && !isGabOnlyOrder && (
            <>
              {/* Lab Results Carousel - Paginated by Study */}
              {labTests.length > 0 && (
                <div className="test-section">
                  <h6 className="section-title">
                    <FiActivity />
                    Estudios de Laboratorio ({labTests.length})
                  </h6>
                  <LabStudyCarousel labTests={labTests} />
                </div>
              )}

              {/* Questionnaires Carousel */}
              {questionnaireTests.length > 0 && (
                <div className="test-section">
                  <h6 className="section-title">
                    <FiClipboard />
                    Evaluaciones ({questionnaireTests.length})
                  </h6>
                  <QuestionnaireCarousel questionnaires={questionnaireTests} />
                </div>
              )}

              {/* No tests available message */}
              {tests.length === 0 && !hasGabStudies && (
                <div className="no-tests-message">
                  <FiAlertCircle size={32} />
                  <p>Los datos de analitos no están disponibles para esta interpretación.</p>
                  <small className="text-muted">
                    Esta interpretación fue generada con una versión anterior del sistema.
                    Puede regenerar la interpretación para obtener los datos completos.
                  </small>
                </div>
              )}
            </>
          )}

          {/* GAB Only Order */}
          {isGabOnlyOrder && hasGabStudies && (
            <GabStudiesPanel
              interpretationId={interpretation.interpretation_id}
              gabStudies={gabStudiesData}
              gabStudiesList={gabStudiesList}
            />
          )}

          {/* GAB Only but no data message */}
          {isGabOnlyOrder && !hasGabStudies && (
            <div className="no-tests-message">
              <FiImage size={32} />
              <p>Los estudios de gabinete no están disponibles para esta interpretación.</p>
              <small className="text-muted">
                No se encontraron estudios de gabinete asociados.
              </small>
            </div>
          )}
        </div>

        {/* Right Panel - Interpretation Wizard */}
        <InterpretationWizard
          interpretationText={interpretationText}
          aiConfidence={ai_confidence}
          actionPlan={action_plan}
          activeStep={wizardStep}
          onStepChange={(newStep: number) => {
            auditLogger.logStepChange(wizardStep, newStep);
            setWizardStep(newStep);
          }}
          status={interpretation.status}
          auditorId={interpretation.auditor_id}
          auditorName={interpretation.auditor_name}
          auditorEmail={interpretation.auditor_email}
          auditorPhotoUrl={interpretation.auditor_photo_url}
          reviewedAt={interpretation.reviewed_at}
          rejectedBy={interpretation.rejected_by}
          rejectedAt={interpretation.rejected_at}
          rejectorName={interpretation.rejector_name}
          rejectorEmail={interpretation.rejector_email}
          rejectorPhotoUrl={interpretation.rejector_photo_url}
        />
      </div>

      {/* Audit Timeline Drawer */}
      {id && (
        <AuditTimeline
          interpretationId={id}
          show={showAuditTimeline}
          onHide={() => setShowAuditTimeline(false)}
        />
      )}

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          auditLogger.logEditCancel();
          setShowEditModal(false);
          setEditViewMode('preview');
        }}
        size={wizardStep === 1 ? 'xl' : 'lg'}
        className={`edit-interpretation-modal ${wizardStep === 1 ? 'two-column-layout' : 'single-column-layout'}`}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {wizardStep === 0 ? (
              <>
                <FiActivity className="me-2" />
                Editar Interpretación Médica
              </>
            ) : (
              <>
                <FiClipboard className="me-2" />
                Editar Plan de Acción
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Interpretation Editor (Step 0) */}
            {wizardStep === 0 && (
              <Form.Group className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="mb-0">Interpretación</Form.Label>
                  <div className="btn-group btn-group-sm">
                    <Button
                      variant={editViewMode === 'preview' ? 'primary' : 'outline-secondary'}
                      onClick={() => setEditViewMode('preview')}
                      size="sm"
                    >
                      <FiEye className="me-1" />
                      Vista Previa
                    </Button>
                    <Button
                      variant={editViewMode === 'edit' ? 'primary' : 'outline-secondary'}
                      onClick={() => setEditViewMode('edit')}
                      size="sm"
                    >
                      <FiCode className="me-1" />
                      Editar Código
                    </Button>
                  </div>
                </div>

                {editViewMode === 'preview' ? (
                  <div className="wysiwyg-editor-container">
                    {/* Toolbar WYSIWYG */}
                    <div className="wysiwyg-toolbar">
                      <div className="toolbar-group">
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => applyFormat('bold')}
                          title="Negrita"
                        >
                          <FiBold />
                        </button>
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => applyFormat('italic')}
                          title="Cursiva"
                        >
                          <FiItalic />
                        </button>
                      </div>

                      <div className="toolbar-divider" />

                      <div className="toolbar-group">
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => applyFormat('formatBlock', 'h1')}
                          title="Título 1"
                        >
                          <LuHeading1 />
                        </button>
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => applyFormat('formatBlock', 'h2')}
                          title="Título 2"
                        >
                          <LuHeading2 />
                        </button>
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => applyFormat('formatBlock', 'h3')}
                          title="Título 3"
                        >
                          <LuHeading3 />
                        </button>
                      </div>

                      <div className="toolbar-divider" />

                      <div className="toolbar-group">
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => applyFormat('insertUnorderedList')}
                          title="Lista con viñetas"
                        >
                          <FiList />
                        </button>
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => applyFormat('insertOrderedList')}
                          title="Lista numerada"
                        >
                          <LuListOrdered />
                        </button>
                      </div>

                      <div className="toolbar-divider" />

                      <div className="toolbar-group">
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => applyFormat('insertHorizontalRule')}
                          title="Línea horizontal"
                        >
                          <FiMinus />
                        </button>
                      </div>
                    </div>

                    {/* Editor Content */}
                    <div
                      ref={setEditorRef}
                      className="wysiwyg-content prose"
                      contentEditable
                      suppressContentEditableWarning
                      onInput={handleEditorInput}
                    />
                  </div>
                ) : (
                  <Form.Control
                    as="textarea"
                    value={editedInterpretation}
                    onChange={(e) => setEditedInterpretation(e.target.value)}
                    className="font-monospace interpretation-textarea"
                  />
                )}

                {editViewMode === 'preview' && (
                  <Form.Text className="text-muted">
                    Use la barra de herramientas para aplicar formato. Para sintaxis markdown avanzada, use "Editar Código".
                  </Form.Text>
                )}
              </Form.Group>
            )}

            {/* Action Plan Editor (Step 1) - Two Column Layout */}
            {wizardStep === 1 && (
              <Row>
                {/* Columna 1: Resumen y Recomendaciones */}
                <Col lg={7} className="edit-column-primary">
                  <Form.Group className="mb-3">
                    <Form.Label>Resumen del Plan</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editedActionPlan.summary}
                      onChange={(e) => setEditedActionPlan(prev => ({ ...prev, summary: e.target.value }))}
                      placeholder="Resumen general del plan de acción..."
                    />
                  </Form.Group>

                  {/* Tabs para alternar entre Recomendaciones y Seguimiento */}
                  <div className="action-plan-tabs">
                    <button
                      type="button"
                      className={`action-plan-tab ${actionPlanSection === 'recommendations' ? 'active' : ''}`}
                      onClick={() => setActionPlanSection('recommendations')}
                    >
                      <FiClipboard className="me-2" />
                      Recomendaciones
                      <Badge bg="light" text="dark" className="ms-2">
                        {editedActionPlan.recommendations.length}
                      </Badge>
                    </button>
                    <button
                      type="button"
                      className={`action-plan-tab ${actionPlanSection === 'follow_up' ? 'active' : ''}`}
                      onClick={() => setActionPlanSection('follow_up')}
                    >
                      <FiActivity className="me-2" />
                      Seguimiento
                      {(editedActionPlan.follow_up?.suggested_tests?.length || 0) > 0 && (
                        <Badge bg="light" text="dark" className="ms-2">
                          {editedActionPlan.follow_up?.suggested_tests?.length}
                        </Badge>
                      )}
                    </button>
                  </div>

                  {/* Contenido de Recomendaciones */}
                  {actionPlanSection === 'recommendations' && (
                  <Form.Group className="action-plan-tab-content">
                    {editedActionPlan.recommendations.length > 0 ? (
                      <div className="recommendations-carousel-editor">
                        {/* Header con Dropdown */}
                        <div className="rec-carousel-header">
                          <Dropdown className="rec-dropdown">
                            <Dropdown.Toggle variant="link" className="rec-dropdown-toggle">
                              <span className="rec-current-title">
                                {editedActionPlan.recommendations[currentRecommendationIdx]?.title || `Recomendación ${currentRecommendationIdx + 1}`}
                              </span>
                              <FiChevronDown className="dropdown-icon" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="rec-dropdown-menu">
                              {editedActionPlan.recommendations.map((rec, idx) => (
                                <Dropdown.Item
                                  key={idx}
                                  active={currentRecommendationIdx === idx}
                                  onClick={() => setCurrentRecommendationIdx(idx)}
                                  className="rec-dropdown-item"
                                >
                                  <span className="rec-item-number">{idx + 1}</span>
                                  <span className="rec-item-title">{rec.title || 'Sin título'}</span>
                                  {rec.timeframe && (
                                    <Badge bg="light" text="muted" className="rec-item-time">
                                      {rec.timeframe}
                                    </Badge>
                                  )}
                                </Dropdown.Item>
                              ))}
                            </Dropdown.Menu>
                          </Dropdown>

                          <div className="rec-header-actions">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setEditedActionPlan(prev => ({
                                  ...prev,
                                  recommendations: [...prev.recommendations, { title: '', description: '', timeframe: '' }]
                                }));
                                setCurrentRecommendationIdx(editedActionPlan.recommendations.length);
                              }}
                              title="Agregar recomendación"
                            >
                              + Nueva
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => {
                                setEditedActionPlan(prev => ({
                                  ...prev,
                                  recommendations: prev.recommendations.filter((_, i) => i !== currentRecommendationIdx)
                                }));
                                setCurrentRecommendationIdx(Math.max(0, currentRecommendationIdx - 1));
                              }}
                              title="Eliminar recomendación"
                              disabled={editedActionPlan.recommendations.length === 0}
                            >
                              <FiX />
                            </Button>
                          </div>
                        </div>

                        {/* Editor de la recomendación actual */}
                        {editedActionPlan.recommendations[currentRecommendationIdx] && (
                          <div className="rec-editor-content">
                            <Form.Group className="mb-3">
                              <Form.Label className="small text-muted">Título</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Título de la recomendación"
                                value={editedActionPlan.recommendations[currentRecommendationIdx].title}
                                onChange={(e) => {
                                  const updated = [...editedActionPlan.recommendations];
                                  updated[currentRecommendationIdx] = { ...updated[currentRecommendationIdx], title: e.target.value };
                                  setEditedActionPlan(prev => ({ ...prev, recommendations: updated }));
                                }}
                              />
                            </Form.Group>

                            <Form.Group className="mb-3 rec-description-editor">
                              <Form.Label className="small text-muted">Descripción</Form.Label>
                              <WysiwygEditor
                                value={editedActionPlan.recommendations[currentRecommendationIdx].description}
                                onChange={(value) => {
                                  const updated = [...editedActionPlan.recommendations];
                                  updated[currentRecommendationIdx] = { ...updated[currentRecommendationIdx], description: value };
                                  setEditedActionPlan(prev => ({ ...prev, recommendations: updated }));
                                }}
                                placeholder="Descripción detallada de la recomendación..."
                                compact
                              />
                            </Form.Group>

                            <Form.Group>
                              <Form.Label className="small text-muted">Plazo de tiempo</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Ej: 2 semanas, Inmediato, 1 mes"
                                value={editedActionPlan.recommendations[currentRecommendationIdx].timeframe || ''}
                                onChange={(e) => {
                                  const updated = [...editedActionPlan.recommendations];
                                  updated[currentRecommendationIdx] = { ...updated[currentRecommendationIdx], timeframe: e.target.value };
                                  setEditedActionPlan(prev => ({ ...prev, recommendations: updated }));
                                }}
                              />
                            </Form.Group>
                          </div>
                        )}

                        {/* Controles del carrusel */}
                        {editedActionPlan.recommendations.length > 1 && (
                          <div className="rec-carousel-controls">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => setCurrentRecommendationIdx(prev => Math.max(0, prev - 1))}
                              disabled={currentRecommendationIdx === 0}
                              className="rec-nav-btn"
                            >
                              <FiChevronLeft />
                            </Button>

                            <div className="rec-carousel-dots">
                              {editedActionPlan.recommendations.map((_, idx) => (
                                <button
                                  key={idx}
                                  className={`dot ${currentRecommendationIdx === idx ? 'active' : ''}`}
                                  onClick={() => setCurrentRecommendationIdx(idx)}
                                />
                              ))}
                            </div>

                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => setCurrentRecommendationIdx(prev => Math.min(editedActionPlan.recommendations.length - 1, prev + 1))}
                              disabled={currentRecommendationIdx === editedActionPlan.recommendations.length - 1}
                              className="rec-nav-btn"
                            >
                              <FiChevronRight />
                            </Button>

                            <span className="rec-carousel-info">
                              {currentRecommendationIdx + 1} / {editedActionPlan.recommendations.length}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4 border rounded bg-light">
                        <FiClipboard size={32} className="mb-2 opacity-50" />
                        <p className="mb-2">No hay recomendaciones</p>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setEditedActionPlan(prev => ({
                              ...prev,
                              recommendations: [{ title: '', description: '', timeframe: '' }]
                            }));
                            setCurrentRecommendationIdx(0);
                          }}
                        >
                          + Agregar primera recomendación
                        </Button>
                      </div>
                    )}
                  </Form.Group>
                  )}

                  {/* Contenido de Seguimiento Recomendado */}
                  {actionPlanSection === 'follow_up' && (
                  <Form.Group className="action-plan-tab-content">
                    <div className="follow-up-editor">
                      <Form.Group className="mb-3">
                        <Form.Label className="small text-muted">Fecha sugerida de seguimiento</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ej: 6 semanas, 3 meses, 1 año"
                          value={editedActionPlan.follow_up?.suggested_date || ''}
                          onChange={(e) => setEditedActionPlan(prev => ({
                            ...prev,
                            follow_up: {
                              suggested_tests: prev.follow_up?.suggested_tests || [],
                              suggested_date: e.target.value
                            }
                          }))}
                        />
                      </Form.Group>

                      <Form.Group>
                        <Form.Label className="small text-muted">Exámenes sugeridos</Form.Label>
                        <div className="suggested-tests-editor">
                          <div className="tests-tags">
                            {(editedActionPlan.follow_up?.suggested_tests || []).map((test, idx) => (
                              <Badge
                                key={idx}
                                bg="info"
                                className="test-tag"
                              >
                                {test}
                                <button
                                  type="button"
                                  className="test-tag-remove"
                                  onClick={() => {
                                    setEditedActionPlan(prev => ({
                                      ...prev,
                                      follow_up: {
                                        suggested_date: prev.follow_up?.suggested_date || '',
                                        suggested_tests: (prev.follow_up?.suggested_tests || []).filter((_, i) => i !== idx)
                                      }
                                    }));
                                  }}
                                >
                                  <FiX size={12} />
                                </button>
                              </Badge>
                            ))}
                          </div>
                          <div className="add-test-input">
                            <Form.Control
                              type="text"
                              placeholder="Agregar examen y presionar Enter..."
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const input = e.target as HTMLInputElement;
                                  const value = input.value.trim();
                                  const currentTests = editedActionPlan.follow_up?.suggested_tests || [];
                                  if (value && !currentTests.includes(value)) {
                                    setEditedActionPlan(prev => ({
                                      ...prev,
                                      follow_up: {
                                        suggested_date: prev.follow_up?.suggested_date || '',
                                        suggested_tests: [...(prev.follow_up?.suggested_tests || []), value]
                                      }
                                    }));
                                    input.value = '';
                                  }
                                }
                              }}
                            />
                          </div>
                        </div>
                        <Form.Text className="text-muted">
                          Presione Enter para agregar cada examen
                        </Form.Text>
                      </Form.Group>
                    </div>
                  </Form.Group>
                  )}
                </Col>

                {/* Columna 2: Motivo y Notas */}
                <Col lg={5} className="edit-column-secondary">
                  <div className="audit-section">
                    <h6 className="audit-section-title">
                      <FiEdit3 className="me-2" />
                      Información de Auditoría
                    </h6>

                    <Form.Group className="mb-3">
                      <Form.Label>Motivo de la modificación <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={modificationReason}
                        onChange={(e) => setModificationReason(e.target.value)}
                        required
                      >
                        <option value="">Seleccionar motivo...</option>
                        {EDIT_REASONS.map((reason) => (
                          <option key={reason.value} value={reason.value}>
                            {reason.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>

                    <Form.Group>
                      <Form.Label>
                        Notas del auditor {modificationReason === 'other' && <span className="text-danger">*</span>}
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={6}
                        value={auditorNotes}
                        onChange={(e) => setAuditorNotes(e.target.value)}
                        placeholder={modificationReason === 'other'
                          ? "Describa el motivo de la modificación..."
                          : "Notas adicionales (opcional)..."
                        }
                        required={modificationReason === 'other'}
                      />
                      {modificationReason === 'other' && (
                        <Form.Text className="text-muted">
                          Al seleccionar "Otro motivo", debe especificar el detalle en las notas.
                        </Form.Text>
                      )}
                    </Form.Group>
                  </div>
                </Col>
              </Row>
            )}

            {/* Motivo y Notas para Step 0 (Interpretación) */}
            {wizardStep === 0 && (
              <>
                <hr />

                <Form.Group className="mb-3">
                  <Form.Label>Motivo de la modificación <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={modificationReason}
                    onChange={(e) => setModificationReason(e.target.value)}
                    required
                  >
                    <option value="">Seleccionar motivo...</option>
                    {EDIT_REASONS.map((reason) => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group>
                  <Form.Label>
                    Notas del auditor {modificationReason === 'other' && <span className="text-danger">*</span>}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={auditorNotes}
                    onChange={(e) => setAuditorNotes(e.target.value)}
                    placeholder={modificationReason === 'other'
                      ? "Describa el motivo de la modificación..."
                      : "Notas adicionales (opcional)..."
                    }
                    required={modificationReason === 'other'}
                  />
                  {modificationReason === 'other' && (
                    <Form.Text className="text-muted">
                      Al seleccionar "Otro motivo", debe especificar el detalle en las notas.
                    </Form.Text>
                  )}
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              auditLogger.logEditCancel();
              setShowEditModal(false);
              setEditViewMode('preview');
            }}
          >
            Cancelar
          </Button>
          {/* The action that was missing. It saves and closes -- no approval, no
              email. Enabled WITHOUT a modification reason on purpose: a reason
              belongs to sending, and demanding one to save would put the doctor
              back in front of the send button they were trying to avoid. */}
          <Button
            variant="outline-primary"
            onClick={handleSaveDraft}
            disabled={savingDraft || !editedInterpretation.trim()}
          >
            <FiSave className="me-2" />
            {savingDraft ? 'Guardando borrador...' : 'Guardar borrador'}
          </Button>
          {/* Renamed from "Guardar y Aprobar". A doctor read that as "save my work
              and come back tomorrow", and it mailed the report to the patient.

              The Drive branch cannot go back to that wording for the same reason, even
              though nothing is mailed there: the misreading was "this only saves", and
              the approval is still immediate and irreversible. It carries the words of
              the confirm button it opens instead, so the two agree. */}
          <Button
            variant="primary"
            onClick={() => setConfirmSendWithEdit(true)}
            disabled={!modificationReason || (modificationReason === 'other' && !auditorNotes.trim()) || approving}
          >
            {approving
              ? 'Guardando...'
              : skipsEmailDelivery
              ? 'Guardar y aprobar sin enviar'
              : 'Guardar y enviar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send confirmation.
          Approving generates the PDF and mails it to the patient in the same step,
          with nothing in between. Until now neither button said so, and a doctor
          who meant to save a draft sent a report to a patient. This is the gate. */}
      <Modal
        show={confirmSendWithEdit !== null}
        onHide={() => setConfirmSendWithEdit(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          {/* A mail icon over an action that mails nothing is exactly the lie the
              confirmation gate exists to end, so the Drive branch carries the icon
              of the action that DOES happen. */}
          <Modal.Title className="h5 d-flex align-items-center gap-2">
            {skipsEmailDelivery ? (
              <FiDownload className="text-primary" />
            ) : (
              <FiMail className="text-primary" />
            )}
            {skipsEmailDelivery
              ? 'Se aprobará sin enviar correo'
              : 'Se enviará el reporte al paciente'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {skipsEmailDelivery ? (
            <>
              {/* Why nothing is mailed, named -- the auditor has to be able to tell
                  a deliberate arrangement from a system that lost the address. */}
              <p>
                <strong>{companyLabel}</strong> no recibe los reportes por correo:
                Previta los descarga y los sube al Drive de la empresa.
              </p>
              {/* The report EXISTS; only the mail does not happen. Nothing here says
                  "no se generó": the PDF is the thing the operator is about to go
                  and fetch. */}
              <p>
                Al continuar, la interpretación queda aprobada y el reporte{' '}
                <strong>queda disponible para descargar</strong>. No se le enviará
                nada a{' '}
                <strong>{patient_info?.name || interpretation.patient_id}</strong>.
              </p>
            </>
          ) : (
            <p>
              Al continuar, la interpretación queda aprobada y el sistema{' '}
              <strong>genera el PDF y lo envía por correo</strong> a{' '}
              <strong>{patient_info?.name || interpretation.patient_id}</strong>.
            </p>
          )}
          <p className="mb-3">
            {/* Approving is irreversible whether or not the mail goes out, so this
                sentence is the same in both branches. */}
            Es inmediato y no se puede deshacer.{' '}
            {/* WHICH of the two texts goes out. One line, never the text itself: a
                confirmation gate that turns into a reading surface is a modal people
                close without reading. The third case is the one that used to say
                nothing -- and silence is dangerous now that a saved draft can be what
                gets sent. */}
            {confirmSendWithEdit
              ? `${textSourceVerb} el texto con tus ediciones.`
              : interpretation.draft_interpretation
              ? `${textSourceVerb} tu versión editada, guardada el ${safeFormatDate(
                  interpretation.draft_saved_at,
                  "d 'de' MMMM"
                )} a las ${safeFormatDate(interpretation.draft_saved_at, 'HH:mm')}.`
              : `${textSourceVerb} el texto generado por la IA, sin ediciones.`}
          </p>
          {/* The step that actually delivers the report. Without it the dialog tells
              the auditor what will NOT happen and leaves the patient with nothing. */}
          {skipsEmailDelivery && (
            <p className="mb-3">
              Para que llegue, descarga el PDF con <strong>Descargar</strong> y
              súbelo al Drive de la empresa.
            </p>
          )}
          {/* An expired review window makes the two sentences above FALSE, and a
              warning that only said "the window expired" would leave the doctor
              choosing between two claims of the same dialog. The approval is still
              recorded, but the callback fails with TaskTimedOut and no PDF is
              generated or mailed -- which is how a report went missing without anyone
              noticing. It warns; it does not block: the clinical decision stays with
              the doctor, who now makes it knowing what will actually happen. */}
          {isReviewWindowExpired(interpretation) && (
            <div className="d-flex align-items-start gap-2 p-2 mb-3 rounded border border-danger bg-danger-subtle">
              <FiAlertTriangle className="text-danger flex-shrink-0 mt-1" />
              <div>
                <div className="fw-bold text-danger">
                  La ventana de revisión venció{' '}
                  {safeFormatDate(interpretation.review_window_expires_at, "d 'de' MMMM 'a las' HH:mm")}
                </div>
                <small className="d-block">
                  Corrige lo anterior: aunque la aprobación quede registrada,{' '}
                  <strong>lo más probable es que no se envíe nada al paciente</strong> —
                  no se generará el PDF ni saldrá el correo.
                </small>
                <small className="d-block mt-1">
                  Si quieres que el reporte llegue, cancela y usa{' '}
                  <strong>Regenerar</strong>: abre una ventana de revisión nueva.
                </small>
              </div>
            </div>
          )}
          <div className="d-flex align-items-center gap-2 p-2 rounded bg-light">
            <FiEye className="text-info flex-shrink-0" />
            <small className="text-muted">
              ¿Quieres revisar antes qué recibirá? Cancela y usa{' '}
              <strong>Vista previa</strong>: abre el PDF real sin enviarlo.
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmSendWithEdit(null)}>
            Cancelar
          </Button>
          <Button
            variant="success"
            disabled={approving}
            onClick={() => {
              const withEdit = confirmSendWithEdit === true;
              setConfirmSendWithEdit(null);
              // The one fork this feature adds. A case the AI read without age or
              // sex does not approve from here: it hands over to the dialog that
              // NAMES what was missing. Everything else -- the complete case, which
              // is the overwhelming majority -- falls straight through to the same
              // `handleApprove` call it has always made.
              if (blindDemographics.length > 0) {
                setBlindAcknowledged(false);
                setConfirmBlindWithEdit(withEdit);
                return;
              }
              handleApprove(withEdit);
            }}
          >
            {skipsEmailDelivery ? (
              <FiDownload className="me-2" />
            ) : (
              <FiMail className="me-2" />
            )}
            {skipsEmailDelivery
              ? confirmSendWithEdit
                ? 'Guardar y aprobar sin enviar'
                : 'Aprobar sin enviar'
              : confirmSendWithEdit
              ? 'Guardar y enviar'
              : 'Aprobar y enviar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Blind-demographics confirmation (E99-F14).
          The auditor KEEPS the ability to approve a case the AI read without age or
          sex -- the clinical call stays theirs. What they lose is the ability to do
          it without noticing. It is a dialog of its own rather than one more warning
          inside the send confirmation, which already carries the delivery policy, the
          irreversibility and the expired-window notice: a fourth paragraph in that
          stack is a paragraph nobody reads, and "un aviso que se firma sin fricción
          no es una barrera, es documentación del daño". */}
      <Modal
        show={confirmBlindWithEdit !== null}
        onHide={() => setConfirmBlindWithEdit(null)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title className="h5 d-flex align-items-center gap-2">
            <FiAlertTriangle className="text-warning" />
            Se interpretó sin {blindPhrase}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body data-testid="blind-demographics-body">
          {/* WHICH one is missing, never a generic "faltan datos del paciente": the
              auditor has to be able to go and check the right field. */}
          <p>
            La IA generó esta interpretación de{' '}
            <strong>{patient_info?.name || interpretation.patient_id}</strong> sin{' '}
            <strong>{blindPhrase}</strong>.
          </p>
          {/* Why it matters, in the auditor's terms. Without this the dialog is a
              data-quality nag and gets dismissed as one. */}
          <p>
            {blindDemographics.length > 1 ? 'Esos dos datos mueven' : 'Ese dato mueve'}{' '}
            los <strong>rangos de referencia</strong>: lo que leyó la IA puede no ser
            lo que corresponde a este paciente.
          </p>
          <p className="mb-3">
            Si {blindDemographics.length > 1 ? 'los conoces' : 'lo conoces'}, cancela y
            usa <strong>Regenerar</strong> para pedir una lectura con{' '}
            {blindDemographics.length > 1 ? 'ellos' : 'él'}. Puedes aprobar igualmente,
            pero quedará firmada con esa carencia.
          </p>
          <Form.Check
            type="checkbox"
            id="blind-demographics-ack"
            checked={blindAcknowledged}
            onChange={(e) => setBlindAcknowledged(e.currentTarget.checked)}
            label={`Confirmo que apruebo esta interpretación aun sabiendo que se generó sin ${blindPhrase}.`}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmBlindWithEdit(null)}>
            Cancelar
          </Button>
          <Button
            variant="warning"
            disabled={!blindAcknowledged || approving}
            onClick={() => {
              const withEdit = confirmBlindWithEdit === true;
              setConfirmBlindWithEdit(null);
              handleApprove(withEdit);
            }}
          >
            <FiCheck className="me-2" />
            Aprobar sin {blindPhrase}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Regenerate Modal */}
      <Modal
        show={showRegenerateModal}
        onHide={() => setShowRegenerateModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Solicitar Nueva Interpretación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Regenerating rebuilds the interpretation from the original lab data. The
              draft does NOT travel to the AI and there is no mechanism to carry it
              over, so it is not "discarded" -- it is lost, with no way back. The only
              channel that reaches the AI is the description below, which is why the
              notice says so. Telling the auditor before they decide is the whole point
              of this feature; a button that destroys work without saying so is the
              pattern it exists to end. */}
          {interpretation.draft_interpretation && (
            <div className="alert alert-warning d-flex gap-2" role="alert">
              <FiAlertTriangle className="flex-shrink-0 mt-1" />
              <div>
                <strong>Se perderá tu borrador y no podrá recuperarse.</strong>
                <div className="small mt-1">
                  El borrador guardado por{' '}
                  {interpretation.draft_saved_by_name || 'un auditor'} el{' '}
                  {safeFormatDate(interpretation.draft_saved_at, "d 'de' MMMM 'a las' HH:mm")}{' '}
                  se elimina al regenerar: la IA reescribe la interpretación desde los
                  datos originales y tus correcciones no viajan con ella. Si quieres que
                  se reflejen, escríbelas en la descripción del ajuste.
                </div>
              </div>
            </div>
          )}

          <p className="text-muted mb-3">¿Qué necesita ajustar?</p>

          <Form.Group className="mb-3">
            <Form.Label>Categoría <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={regenerateCategory}
              onChange={(e) => setRegenerateCategory(e.target.value)}
            >
              <option value="">Seleccionar categoría...</option>
              {REGENERATE_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descripción del ajuste <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              value={regenerateDescription}
              onChange={(e) => setRegenerateDescription(e.target.value)}
              placeholder="Describa qué debe cambiar la IA en la interpretación..."
            />
            <Form.Text className="text-muted">
              Mínimo 10 caracteres
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowRegenerateModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="warning"
            onClick={handleRegenerate}
            disabled={!regenerateCategory || regenerateDescription.length < 10 || regenerating}
          >
            {regenerating
              ? 'Solicitando...'
              : interpretation.draft_interpretation
              ? 'Regenerar y descartar borrador'
              : 'Solicitar Nueva'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reject Modal */}
      <Modal
        show={showRejectModal}
        onHide={() => setShowRejectModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <FiXCircle className="me-2" />
            Rechazar Interpretación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">¿Por qué deseas rechazar esta interpretación?</p>

          <Form.Group className="mb-3">
            <Form.Label>Motivo <span className="text-danger">*</span></Form.Label>
            <Form.Select
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value as RejectionReason | '')}
            >
              <option value="">Seleccionar motivo...</option>
              {REJECTION_REASONS.map(reason => (
                <option key={reason.value} value={reason.value}>{reason.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Comentario {rejectionReason === 'other' && <span className="text-danger">*</span>}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={rejectionComment}
              onChange={(e) => setRejectionComment(e.target.value)}
              placeholder={rejectionReason === 'other' ? 'Explique el motivo del rechazo...' : 'Comentario adicional (opcional)...'}
            />
            {rejectionReason === 'other' && (
              <Form.Text className="text-muted">
                Obligatorio cuando selecciona "Otro"
              </Form.Text>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowRejectModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={handleReject}
            disabled={
              !rejectionReason ||
              (rejectionReason === 'other' && !rejectionComment.trim()) ||
              rejecting
            }
          >
            {rejecting ? 'Rechazando...' : 'Rechazar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {interpretation && (
        <ReopenModal
          show={showReopenModal}
          interpretationId={interpretation.interpretation_id}
          onSuccess={() => {
            setShowReopenModal(false);
            interpretationsApi.getById(interpretation.interpretation_id).then(setInterpretation);
          }}
          onCancel={() => setShowReopenModal(false)}
        />
      )}
    </div>
  );
};

export default InterpretationDetail;
